import React, { createContext, useContext, memo, useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { Box, Button, styled, ListItem as ListItem$1, Typography, Grid, ListItemAvatar as ListItemAvatar$1, ListItemText, List as List$1, Stack, FormHelperText, InputAdornment, Container as Container$1, Checkbox, Switch, Tooltip, Select, MenuItem, ListItemButton as ListItemButton$1 } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SortOption, SortPopoverMenu, MicOnIcon as MicOnIcon$1, useDateFormat, ParticipantAvatar, MicOffIcon, joinSuccess, AutomodSelectionStrategy, sortParticipantsWithConfig, generateUniquedId, notifications, notificationAction, isEnumErrorStruct, createSignalingApiCall, TextField, SearchIcon, DurationField, formikDurationFieldProps, CommonFormItem, formikSwitchProps, formikProps, BackIcon, getCurrentTimezone, DurationIcon, ProgressBar, LegalBallotIcon, NoVotesIcon, AccordionItem, CopyIcon } from '@opentalk/common';
import { useSelector, useDispatch } from 'react-redux';
import { TrapFocus } from '@mui/base';
import { useFormikContext, Form as Form$1 } from 'formik';
import { FormikWizard } from 'formik-wizard-form';
import { get, debounce, find, isEmpty, truncate } from 'lodash';
import * as yup from 'yup';

var AutomodContext = createContext({
    children: null,
    configurationParticipants: []
});
var AutomodProvider = function (_a) {
    var apiMessages = _a.apiMessages, children = _a.children, configurationParticipants = _a.configurationParticipants;
    return (React.createElement(AutomodContext.Provider, { value: {
            apiMessages: apiMessages,
            configurationParticipants: configurationParticipants
        } }, children));
};
function useAutomodContext() {
    return useContext(AutomodContext);
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray$1(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

// SPDX-FileCopyrightText: OpenTalk GmbH <mail@opentalk.eu>
var items = [
    {
        i18nKey: "sort-name-asc",
        type: SortOption.NameASC
    },
    {
        i18nKey: "sort-name-dsc",
        type: SortOption.NameDESC
    },
    {
        i18nKey: "sort-first-join",
        type: SortOption.FirstJoin
    },
    {
        i18nKey: "sort-last-join",
        type: SortOption.LastJoin
    },
    {
        i18nKey: "sort-random",
        type: SortOption.Random
    },
];
var SortField = function (_a) {
    var selectedSortType = _a.selectedSortType, onChange = _a.onChange;
    var id = "talking-stick-sort-popover";
    var t = useTranslation().t;
    var _b = useState(false), expanded = _b[0], setExpanded = _b[1];
    var anchor = useRef(null);
    var selectedItem = useMemo(function () {
        return items.find(function (item) { return item.type === selectedSortType; });
    }, [selectedSortType]);
    var toggleExpandedState = function () { return setExpanded(function (current) { return !current; }); };
    return (React.createElement(Box, null,
        React.createElement(Button, { ref: anchor, type: "button", "aria-expanded": expanded, "aria-controls": id, "aria-haspopup": "menu", onClick: toggleExpandedState, color: "secondary" }, t(selectedItem.i18nKey)),
        anchor.current && expanded && (React.createElement(SortPopoverMenu, { id: id, anchorEl: anchor.current, isOpen: true, items: items, onClose: function () { return setExpanded(false); }, selectedOptionType: selectedSortType, onChange: onChange }))));
};
var SortField$1 = memo(SortField);

var StyledListItem = styled(ListItem$1, {
    shouldForwardProp: function (prop) {
        return !["isActiveSpeaker", "isCurrentUser"].includes(prop);
    }
})(function (_a) {
    var theme = _a.theme, isActiveSpeaker = _a.isActiveSpeaker, isCurrentUser = _a.isCurrentUser;
    var styles = {
        padding: 0,
        border: "1px solid",
        borderRadius: theme.spacing(1),
        backgroundColor: isActiveSpeaker ? "#1A2D33" : "unset",
        borderColor: "transparent"
    };
    if (isCurrentUser) {
        styles.borderColor = "#949493";
    }
    if (isActiveSpeaker) {
        styles.borderColor = theme.palette.primary.main;
    }
    return styles;
});
var JoinedText = styled(Typography)(function (_a) {
    var theme = _a.theme;
    return ({
        color: theme.palette.primary.contrastText
    });
});
var MicOnIcon = styled(MicOnIcon$1)(function (_a) {
    var theme = _a.theme;
    return ({
        color: theme.palette.primary.main
    });
});
var ParticipantListItem = function (_a) {
    var avatarUrl = _a.avatarUrl, displayName = _a.displayName, joinedAt = _a.joinedAt, isActiveSpeaker = _a.isActiveSpeaker, isCurrentUser = _a.isCurrentUser;
    var t = useTranslation().t;
    var joinedTimestamp = new Date(joinedAt !== null && joinedAt !== void 0 ? joinedAt : new Date());
    var formattedJoinedTime = useDateFormat(joinedTimestamp, "time");
    return (React.createElement(StyledListItem, { isActiveSpeaker: isActiveSpeaker, isCurrentUser: isCurrentUser },
        React.createElement(Grid, { container: true, gap: 1, direction: "row", wrap: "nowrap", alignItems: "center" },
            React.createElement(Grid, { item: true },
                React.createElement(ListItemAvatar$1, null,
                    React.createElement(ParticipantAvatar, { src: avatarUrl }, displayName))),
            React.createElement(Grid, { item: true, xs: true, zeroMinWidth: true },
                React.createElement(ListItemText, { primary: React.createElement(Typography, { variant: "body1", noWrap: true }, displayName), secondary: React.createElement(JoinedText, { variant: "caption" }, t("participant-joined-text", {
                        joinedTime: formattedJoinedTime
                    })) })),
            React.createElement(Grid, { item: true }, isActiveSpeaker ? React.createElement(MicOnIcon, null) : React.createElement(MicOffIcon, { disabled: true })))));
};
var ParticipantListItem$1 = memo(ParticipantListItem);

function n(n){for(var r=arguments.length,t=Array(r>1?r-1:0),e=1;e<r;e++)t[e-1]=arguments[e];if("production"!==process.env.NODE_ENV){var i=Y[n],o=i?"function"==typeof i?i.apply(null,t):i:"unknown error nr: "+n;throw Error("[Immer] "+o)}throw Error("[Immer] minified error nr: "+n+(t.length?" "+t.map((function(n){return "'"+n+"'"})).join(","):"")+". Find the full error at: https://bit.ly/3cXEKWf")}function r(n){return !!n&&!!n[Q]}function t(n){var r;return !!n&&(function(n){if(!n||"object"!=typeof n)return !1;var r=Object.getPrototypeOf(n);if(null===r)return !0;var t=Object.hasOwnProperty.call(r,"constructor")&&r.constructor;return t===Object||"function"==typeof t&&Function.toString.call(t)===Z}(n)||Array.isArray(n)||!!n[L]||!!(null===(r=n.constructor)||void 0===r?void 0:r[L])||s(n)||v(n))}function i(n,r,t){void 0===t&&(t=!1),0===o(n)?(t?Object.keys:nn)(n).forEach((function(e){t&&"symbol"==typeof e||r(e,n[e],n);})):n.forEach((function(t,e){return r(e,t,n)}));}function o(n){var r=n[Q];return r?r.i>3?r.i-4:r.i:Array.isArray(n)?1:s(n)?2:v(n)?3:0}function u(n,r){return 2===o(n)?n.has(r):Object.prototype.hasOwnProperty.call(n,r)}function a(n,r){return 2===o(n)?n.get(r):n[r]}function f(n,r,t){var e=o(n);2===e?n.set(r,t):3===e?n.add(t):n[r]=t;}function c(n,r){return n===r?0!==n||1/n==1/r:n!=n&&r!=r}function s(n){return X&&n instanceof Map}function v(n){return q&&n instanceof Set}function p(n){return n.o||n.t}function l(n){if(Array.isArray(n))return Array.prototype.slice.call(n);var r=rn(n);delete r[Q];for(var t=nn(r),e=0;e<t.length;e++){var i=t[e],o=r[i];!1===o.writable&&(o.writable=!0,o.configurable=!0),(o.get||o.set)&&(r[i]={configurable:!0,writable:!0,enumerable:o.enumerable,value:n[i]});}return Object.create(Object.getPrototypeOf(n),r)}function d(n,e){return void 0===e&&(e=!1),y(n)||r(n)||!t(n)||(o(n)>1&&(n.set=n.add=n.clear=n.delete=h),Object.freeze(n),e&&i(n,(function(n,r){return d(r,!0)}),!0)),n}function h(){n(2);}function y(n){return null==n||"object"!=typeof n||Object.isFrozen(n)}function b(r){var t=tn[r];return t||n(18,r),t}function m(n,r){tn[n]||(tn[n]=r);}function _(){return "production"===process.env.NODE_ENV||U||n(0),U}function j(n,r){r&&(b("Patches"),n.u=[],n.s=[],n.v=r);}function O(n){g(n),n.p.forEach(S),n.p=null;}function g(n){n===U&&(U=n.l);}function w(n){return U={p:[],l:U,h:n,m:!0,_:0}}function S(n){var r=n[Q];0===r.i||1===r.i?r.j():r.O=!0;}function P(r,e){e._=e.p.length;var i=e.p[0],o=void 0!==r&&r!==i;return e.h.g||b("ES5").S(e,r,o),o?(i[Q].P&&(O(e),n(4)),t(r)&&(r=M(e,r),e.l||x(e,r)),e.u&&b("Patches").M(i[Q].t,r,e.u,e.s)):r=M(e,i,[]),O(e),e.u&&e.v(e.u,e.s),r!==H?r:void 0}function M(n,r,t){if(y(r))return r;var e=r[Q];if(!e)return i(r,(function(i,o){return A(n,e,r,i,o,t)}),!0),r;if(e.A!==n)return r;if(!e.P)return x(n,e.t,!0),e.t;if(!e.I){e.I=!0,e.A._--;var o=4===e.i||5===e.i?e.o=l(e.k):e.o,u=o,a=!1;3===e.i&&(u=new Set(o),o.clear(),a=!0),i(u,(function(r,i){return A(n,e,o,r,i,t,a)})),x(n,o,!1),t&&n.u&&b("Patches").N(e,t,n.u,n.s);}return e.o}function A(e,i,o,a,c,s,v){if("production"!==process.env.NODE_ENV&&c===o&&n(5),r(c)){var p=M(e,c,s&&i&&3!==i.i&&!u(i.R,a)?s.concat(a):void 0);if(f(o,a,p),!r(p))return;e.m=!1;}else v&&o.add(c);if(t(c)&&!y(c)){if(!e.h.D&&e._<1)return;M(e,c),i&&i.A.l||x(e,c);}}function x(n,r,t){void 0===t&&(t=!1),n.h.D&&n.m&&d(r,t);}function z(n,r){var t=n[Q];return (t?p(t):n)[r]}function I(n,r){if(r in n)for(var t=Object.getPrototypeOf(n);t;){var e=Object.getOwnPropertyDescriptor(t,r);if(e)return e;t=Object.getPrototypeOf(t);}}function k(n){n.P||(n.P=!0,n.l&&k(n.l));}function E(n){n.o||(n.o=l(n.t));}function N(n,r,t){var e=s(r)?b("MapSet").F(r,t):v(r)?b("MapSet").T(r,t):n.g?function(n,r){var t=Array.isArray(n),e={i:t?1:0,A:r?r.A:_(),P:!1,I:!1,R:{},l:r,t:n,k:null,o:null,j:null,C:!1},i=e,o=en;t&&(i=[e],o=on);var u=Proxy.revocable(i,o),a=u.revoke,f=u.proxy;return e.k=f,e.j=a,f}(r,t):b("ES5").J(r,t);return (t?t.A:_()).p.push(e),e}function R(e){return r(e)||n(22,e),function n(r){if(!t(r))return r;var e,u=r[Q],c=o(r);if(u){if(!u.P&&(u.i<4||!b("ES5").K(u)))return u.t;u.I=!0,e=D(r,c),u.I=!1;}else e=D(r,c);return i(e,(function(r,t){u&&a(u.t,r)===t||f(e,r,n(t));})),3===c?new Set(e):e}(e)}function D(n,r){switch(r){case 2:return new Map(n);case 3:return Array.from(n)}return l(n)}function F(){function t(n,r){var t=s[n];return t?t.enumerable=r:s[n]=t={configurable:!0,enumerable:r,get:function(){var r=this[Q];return "production"!==process.env.NODE_ENV&&f(r),en.get(r,n)},set:function(r){var t=this[Q];"production"!==process.env.NODE_ENV&&f(t),en.set(t,n,r);}},t}function e(n){for(var r=n.length-1;r>=0;r--){var t=n[r][Q];if(!t.P)switch(t.i){case 5:a(t)&&k(t);break;case 4:o(t)&&k(t);}}}function o(n){for(var r=n.t,t=n.k,e=nn(t),i=e.length-1;i>=0;i--){var o=e[i];if(o!==Q){var a=r[o];if(void 0===a&&!u(r,o))return !0;var f=t[o],s=f&&f[Q];if(s?s.t!==a:!c(f,a))return !0}}var v=!!r[Q];return e.length!==nn(r).length+(v?0:1)}function a(n){var r=n.k;if(r.length!==n.t.length)return !0;var t=Object.getOwnPropertyDescriptor(r,r.length-1);if(t&&!t.get)return !0;for(var e=0;e<r.length;e++)if(!r.hasOwnProperty(e))return !0;return !1}function f(r){r.O&&n(3,JSON.stringify(p(r)));}var s={};m("ES5",{J:function(n,r){var e=Array.isArray(n),i=function(n,r){if(n){for(var e=Array(r.length),i=0;i<r.length;i++)Object.defineProperty(e,""+i,t(i,!0));return e}var o=rn(r);delete o[Q];for(var u=nn(o),a=0;a<u.length;a++){var f=u[a];o[f]=t(f,n||!!o[f].enumerable);}return Object.create(Object.getPrototypeOf(r),o)}(e,n),o={i:e?5:4,A:r?r.A:_(),P:!1,I:!1,R:{},l:r,t:n,k:i,o:null,O:!1,C:!1};return Object.defineProperty(i,Q,{value:o,writable:!0}),i},S:function(n,t,o){o?r(t)&&t[Q].A===n&&e(n.p):(n.u&&function n(r){if(r&&"object"==typeof r){var t=r[Q];if(t){var e=t.t,o=t.k,f=t.R,c=t.i;if(4===c)i(o,(function(r){r!==Q&&(void 0!==e[r]||u(e,r)?f[r]||n(o[r]):(f[r]=!0,k(t)));})),i(e,(function(n){void 0!==o[n]||u(o,n)||(f[n]=!1,k(t));}));else if(5===c){if(a(t)&&(k(t),f.length=!0),o.length<e.length)for(var s=o.length;s<e.length;s++)f[s]=!1;else for(var v=e.length;v<o.length;v++)f[v]=!0;for(var p=Math.min(o.length,e.length),l=0;l<p;l++)o.hasOwnProperty(l)||(f[l]=!0),void 0===f[l]&&n(o[l]);}}}}(n.p[0]),e(n.p));},K:function(n){return 4===n.i?o(n):a(n)}});}var G,U,W="undefined"!=typeof Symbol&&"symbol"==typeof Symbol("x"),X="undefined"!=typeof Map,q="undefined"!=typeof Set,B="undefined"!=typeof Proxy&&void 0!==Proxy.revocable&&"undefined"!=typeof Reflect,H=W?Symbol.for("immer-nothing"):((G={})["immer-nothing"]=!0,G),L=W?Symbol.for("immer-draftable"):"__$immer_draftable",Q=W?Symbol.for("immer-state"):"__$immer_state",Y={0:"Illegal state",1:"Immer drafts cannot have computed properties",2:"This object has been frozen and should not be mutated",3:function(n){return "Cannot use a proxy that has been revoked. Did you pass an object from inside an immer function to an async process? "+n},4:"An immer producer returned a new value *and* modified its draft. Either return a new value *or* modify the draft.",5:"Immer forbids circular references",6:"The first or second argument to `produce` must be a function",7:"The third argument to `produce` must be a function or undefined",8:"First argument to `createDraft` must be a plain object, an array, or an immerable object",9:"First argument to `finishDraft` must be a draft returned by `createDraft`",10:"The given draft is already finalized",11:"Object.defineProperty() cannot be used on an Immer draft",12:"Object.setPrototypeOf() cannot be used on an Immer draft",13:"Immer only supports deleting array indices",14:"Immer only supports setting array indices and the 'length' property",15:function(n){return "Cannot apply patch, path doesn't resolve: "+n},16:'Sets cannot have "replace" patches.',17:function(n){return "Unsupported patch operation: "+n},18:function(n){return "The plugin for '"+n+"' has not been loaded into Immer. To enable the plugin, import and call `enable"+n+"()` when initializing your application."},20:"Cannot use proxies if Proxy, Proxy.revocable or Reflect are not available",21:function(n){return "produce can only be called on things that are draftable: plain objects, arrays, Map, Set or classes that are marked with '[immerable]: true'. Got '"+n+"'"},22:function(n){return "'current' expects a draft, got: "+n},23:function(n){return "'original' expects a draft, got: "+n},24:"Patching reserved attributes like __proto__, prototype and constructor is not allowed"},Z=""+Object.prototype.constructor,nn="undefined"!=typeof Reflect&&Reflect.ownKeys?Reflect.ownKeys:void 0!==Object.getOwnPropertySymbols?function(n){return Object.getOwnPropertyNames(n).concat(Object.getOwnPropertySymbols(n))}:Object.getOwnPropertyNames,rn=Object.getOwnPropertyDescriptors||function(n){var r={};return nn(n).forEach((function(t){r[t]=Object.getOwnPropertyDescriptor(n,t);})),r},tn={},en={get:function(n,r){if(r===Q)return n;var e=p(n);if(!u(e,r))return function(n,r,t){var e,i=I(r,t);return i?"value"in i?i.value:null===(e=i.get)||void 0===e?void 0:e.call(n.k):void 0}(n,e,r);var i=e[r];return n.I||!t(i)?i:i===z(n.t,r)?(E(n),n.o[r]=N(n.A.h,i,n)):i},has:function(n,r){return r in p(n)},ownKeys:function(n){return Reflect.ownKeys(p(n))},set:function(n,r,t){var e=I(p(n),r);if(null==e?void 0:e.set)return e.set.call(n.k,t),!0;if(!n.P){var i=z(p(n),r),o=null==i?void 0:i[Q];if(o&&o.t===t)return n.o[r]=t,n.R[r]=!1,!0;if(c(t,i)&&(void 0!==t||u(n.t,r)))return !0;E(n),k(n);}return n.o[r]===t&&(void 0!==t||r in n.o)||Number.isNaN(t)&&Number.isNaN(n.o[r])||(n.o[r]=t,n.R[r]=!0),!0},deleteProperty:function(n,r){return void 0!==z(n.t,r)||r in n.t?(n.R[r]=!1,E(n),k(n)):delete n.R[r],n.o&&delete n.o[r],!0},getOwnPropertyDescriptor:function(n,r){var t=p(n),e=Reflect.getOwnPropertyDescriptor(t,r);return e?{writable:!0,configurable:1!==n.i||"length"!==r,enumerable:e.enumerable,value:t[r]}:e},defineProperty:function(){n(11);},getPrototypeOf:function(n){return Object.getPrototypeOf(n.t)},setPrototypeOf:function(){n(12);}},on={};i(en,(function(n,r){on[n]=function(){return arguments[0]=arguments[0][0],r.apply(this,arguments)};})),on.deleteProperty=function(r,t){return "production"!==process.env.NODE_ENV&&isNaN(parseInt(t))&&n(13),on.set.call(this,r,t,void 0)},on.set=function(r,t,e){return "production"!==process.env.NODE_ENV&&"length"!==t&&isNaN(parseInt(t))&&n(14),en.set.call(this,r[0],t,e,r[0])};var un=function(){function e(r){var e=this;this.g=B,this.D=!0,this.produce=function(r,i,o){if("function"==typeof r&&"function"!=typeof i){var u=i;i=r;var a=e;return function(n){var r=this;void 0===n&&(n=u);for(var t=arguments.length,e=Array(t>1?t-1:0),o=1;o<t;o++)e[o-1]=arguments[o];return a.produce(n,(function(n){var t;return (t=i).call.apply(t,[r,n].concat(e))}))}}var f;if("function"!=typeof i&&n(6),void 0!==o&&"function"!=typeof o&&n(7),t(r)){var c=w(e),s=N(e,r,void 0),v=!0;try{f=i(s),v=!1;}finally{v?O(c):g(c);}return "undefined"!=typeof Promise&&f instanceof Promise?f.then((function(n){return j(c,o),P(n,c)}),(function(n){throw O(c),n})):(j(c,o),P(f,c))}if(!r||"object"!=typeof r){if(void 0===(f=i(r))&&(f=r),f===H&&(f=void 0),e.D&&d(f,!0),o){var p=[],l=[];b("Patches").M(r,f,p,l),o(p,l);}return f}n(21,r);},this.produceWithPatches=function(n,r){if("function"==typeof n)return function(r){for(var t=arguments.length,i=Array(t>1?t-1:0),o=1;o<t;o++)i[o-1]=arguments[o];return e.produceWithPatches(r,(function(r){return n.apply(void 0,[r].concat(i))}))};var t,i,o=e.produce(n,r,(function(n,r){t=n,i=r;}));return "undefined"!=typeof Promise&&o instanceof Promise?o.then((function(n){return [n,t,i]})):[o,t,i]},"boolean"==typeof(null==r?void 0:r.useProxies)&&this.setUseProxies(r.useProxies),"boolean"==typeof(null==r?void 0:r.autoFreeze)&&this.setAutoFreeze(r.autoFreeze);}var i=e.prototype;return i.createDraft=function(e){t(e)||n(8),r(e)&&(e=R(e));var i=w(this),o=N(this,e,void 0);return o[Q].C=!0,g(i),o},i.finishDraft=function(r,t){var e=r&&r[Q];"production"!==process.env.NODE_ENV&&(e&&e.C||n(9),e.I&&n(10));var i=e.A;return j(i,t),P(void 0,i)},i.setAutoFreeze=function(n){this.D=n;},i.setUseProxies=function(r){r&&!B&&n(20),this.g=r;},i.applyPatches=function(n,t){var e;for(e=t.length-1;e>=0;e--){var i=t[e];if(0===i.path.length&&"replace"===i.op){n=i.value;break}}e>-1&&(t=t.slice(e+1));var o=b("Patches").$;return r(n)?o(n,t):this.produce(n,(function(n){return o(n,t)}))},e}(),an=new un,fn=an.produce;an.produceWithPatches.bind(an);an.setAutoFreeze.bind(an);an.setUseProxies.bind(an);an.applyPatches.bind(an);an.createDraft.bind(an);an.finishDraft.bind(an);

/**
 * These are private action types reserved by Redux.
 * For any unknown actions, you must return the current state.
 * If the current state is undefined, you must return the initial state.
 * Do not reference these action types directly in your code.
 */
var randomString = function randomString() {
  return Math.random().toString(36).substring(7).split('').join('.');
};

({
  INIT: "@@redux/INIT" + randomString(),
  REPLACE: "@@redux/REPLACE" + randomString(),
  PROBE_UNKNOWN_ACTION: function PROBE_UNKNOWN_ACTION() {
    return "@@redux/PROBE_UNKNOWN_ACTION" + randomString();
  }
});

/**
 * Prints a warning in the console if it exists.
 *
 * @param {String} message The warning message.
 * @returns {void}
 */
function warning(message) {
  /* eslint-disable no-console */
  if (typeof console !== 'undefined' && typeof console.error === 'function') {
    console.error(message);
  }
  /* eslint-enable no-console */


  try {
    // This error was thrown as a convenience so that if you enable
    // "break on all exceptions" in your console,
    // it would pause the execution at this line.
    throw new Error(message);
  } catch (e) {} // eslint-disable-line no-empty

}

/*
 * This is a dummy function to check if the function name has been altered by minification.
 * If the function has been minified and NODE_ENV !== 'production', warn the user.
 */

function isCrushed() {}

if (process.env.NODE_ENV !== 'production' && typeof isCrushed.name === 'string' && isCrushed.name !== 'isCrushed') {
  warning('You are currently using minified code outside of NODE_ENV === "production". ' + 'This means that you are running a slower development build of Redux. ' + 'You can use loose-envify (https://github.com/zertosh/loose-envify) for browserify ' + 'or setting mode to production in webpack (https://webpack.js.org/concepts/mode/) ' + 'to ensure you have the correct code for your production build.');
}

// Cache implementation based on Erik Rasmussen's `lru-memoize`:
// https://github.com/erikras/lru-memoize
var NOT_FOUND = 'NOT_FOUND';

function createSingletonCache(equals) {
  var entry;
  return {
    get: function get(key) {
      if (entry && equals(entry.key, key)) {
        return entry.value;
      }

      return NOT_FOUND;
    },
    put: function put(key, value) {
      entry = {
        key: key,
        value: value
      };
    },
    getEntries: function getEntries() {
      return entry ? [entry] : [];
    },
    clear: function clear() {
      entry = undefined;
    }
  };
}

function createLruCache(maxSize, equals) {
  var entries = [];

  function get(key) {
    var cacheIndex = entries.findIndex(function (entry) {
      return equals(key, entry.key);
    }); // We found a cached entry

    if (cacheIndex > -1) {
      var entry = entries[cacheIndex]; // Cached entry not at top of cache, move it to the top

      if (cacheIndex > 0) {
        entries.splice(cacheIndex, 1);
        entries.unshift(entry);
      }

      return entry.value;
    } // No entry found in cache, return sentinel


    return NOT_FOUND;
  }

  function put(key, value) {
    if (get(key) === NOT_FOUND) {
      // TODO Is unshift slow?
      entries.unshift({
        key: key,
        value: value
      });

      if (entries.length > maxSize) {
        entries.pop();
      }
    }
  }

  function getEntries() {
    return entries;
  }

  function clear() {
    entries = [];
  }

  return {
    get: get,
    put: put,
    getEntries: getEntries,
    clear: clear
  };
}

var defaultEqualityCheck = function defaultEqualityCheck(a, b) {
  return a === b;
};
function createCacheKeyComparator(equalityCheck) {
  return function areArgumentsShallowlyEqual(prev, next) {
    if (prev === null || next === null || prev.length !== next.length) {
      return false;
    } // Do this in a for loop (and not a `forEach` or an `every`) so we can determine equality as fast as possible.


    var length = prev.length;

    for (var i = 0; i < length; i++) {
      if (!equalityCheck(prev[i], next[i])) {
        return false;
      }
    }

    return true;
  };
}
// defaultMemoize now supports a configurable cache size with LRU behavior,
// and optional comparison of the result value with existing values
function defaultMemoize(func, equalityCheckOrOptions) {
  var providedOptions = typeof equalityCheckOrOptions === 'object' ? equalityCheckOrOptions : {
    equalityCheck: equalityCheckOrOptions
  };
  var _providedOptions$equa = providedOptions.equalityCheck,
      equalityCheck = _providedOptions$equa === void 0 ? defaultEqualityCheck : _providedOptions$equa,
      _providedOptions$maxS = providedOptions.maxSize,
      maxSize = _providedOptions$maxS === void 0 ? 1 : _providedOptions$maxS,
      resultEqualityCheck = providedOptions.resultEqualityCheck;
  var comparator = createCacheKeyComparator(equalityCheck);
  var cache = maxSize === 1 ? createSingletonCache(comparator) : createLruCache(maxSize, comparator); // we reference arguments instead of spreading them for performance reasons

  function memoized() {
    var value = cache.get(arguments);

    if (value === NOT_FOUND) {
      // @ts-ignore
      value = func.apply(null, arguments);

      if (resultEqualityCheck) {
        var entries = cache.getEntries();
        var matchingEntry = entries.find(function (entry) {
          return resultEqualityCheck(entry.value, value);
        });

        if (matchingEntry) {
          value = matchingEntry.value;
        }
      }

      cache.put(arguments, value);
    }

    return value;
  }

  memoized.clearCache = function () {
    return cache.clear();
  };

  return memoized;
}

function getDependencies(funcs) {
  var dependencies = Array.isArray(funcs[0]) ? funcs[0] : funcs;

  if (!dependencies.every(function (dep) {
    return typeof dep === 'function';
  })) {
    var dependencyTypes = dependencies.map(function (dep) {
      return typeof dep === 'function' ? "function " + (dep.name || 'unnamed') + "()" : typeof dep;
    }).join(', ');
    throw new Error("createSelector expects all input-selectors to be functions, but received the following types: [" + dependencyTypes + "]");
  }

  return dependencies;
}

function createSelectorCreator(memoize) {
  for (var _len = arguments.length, memoizeOptionsFromArgs = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    memoizeOptionsFromArgs[_key - 1] = arguments[_key];
  }

  var createSelector = function createSelector() {
    for (var _len2 = arguments.length, funcs = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      funcs[_key2] = arguments[_key2];
    }

    var _recomputations = 0;

    var _lastResult; // Due to the intricacies of rest params, we can't do an optional arg after `...funcs`.
    // So, start by declaring the default value here.
    // (And yes, the words 'memoize' and 'options' appear too many times in this next sequence.)


    var directlyPassedOptions = {
      memoizeOptions: undefined
    }; // Normally, the result func or "output selector" is the last arg

    var resultFunc = funcs.pop(); // If the result func is actually an _object_, assume it's our options object

    if (typeof resultFunc === 'object') {
      directlyPassedOptions = resultFunc; // and pop the real result func off

      resultFunc = funcs.pop();
    }

    if (typeof resultFunc !== 'function') {
      throw new Error("createSelector expects an output function after the inputs, but received: [" + typeof resultFunc + "]");
    } // Determine which set of options we're using. Prefer options passed directly,
    // but fall back to options given to createSelectorCreator.


    var _directlyPassedOption = directlyPassedOptions,
        _directlyPassedOption2 = _directlyPassedOption.memoizeOptions,
        memoizeOptions = _directlyPassedOption2 === void 0 ? memoizeOptionsFromArgs : _directlyPassedOption2; // Simplifying assumption: it's unlikely that the first options arg of the provided memoizer
    // is an array. In most libs I've looked at, it's an equality function or options object.
    // Based on that, if `memoizeOptions` _is_ an array, we assume it's a full
    // user-provided array of options. Otherwise, it must be just the _first_ arg, and so
    // we wrap it in an array so we can apply it.

    var finalMemoizeOptions = Array.isArray(memoizeOptions) ? memoizeOptions : [memoizeOptions];
    var dependencies = getDependencies(funcs);
    var memoizedResultFunc = memoize.apply(void 0, [function recomputationWrapper() {
      _recomputations++; // apply arguments instead of spreading for performance.

      return resultFunc.apply(null, arguments);
    }].concat(finalMemoizeOptions)); // If a selector is called with the exact same arguments we don't need to traverse our dependencies again.

    var selector = memoize(function dependenciesChecker() {
      var params = [];
      var length = dependencies.length;

      for (var i = 0; i < length; i++) {
        // apply arguments instead of spreading and mutate a local list of params for performance.
        // @ts-ignore
        params.push(dependencies[i].apply(null, arguments));
      } // apply arguments instead of spreading for performance.


      _lastResult = memoizedResultFunc.apply(null, params);
      return _lastResult;
    });
    Object.assign(selector, {
      resultFunc: resultFunc,
      memoizedResultFunc: memoizedResultFunc,
      dependencies: dependencies,
      lastResult: function lastResult() {
        return _lastResult;
      },
      recomputations: function recomputations() {
        return _recomputations;
      },
      resetRecomputations: function resetRecomputations() {
        return _recomputations = 0;
      }
    });
    return selector;
  }; // @ts-ignore


  return createSelector;
}
var createSelector = /* #__PURE__ */createSelectorCreator(defaultMemoize);

var __extends = (undefined && undefined.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __generator = (undefined && undefined.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (undefined && undefined.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = function (obj, key, value) { return key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value: value }) : obj[key] = value; };
var __spreadValues = function (a, b) {
    for (var prop in b || (b = {}))
        if (__hasOwnProp.call(b, prop))
            __defNormalProp(a, prop, b[prop]);
    if (__getOwnPropSymbols)
        for (var _i = 0, _c = __getOwnPropSymbols(b); _i < _c.length; _i++) {
            var prop = _c[_i];
            if (__propIsEnum.call(b, prop))
                __defNormalProp(a, prop, b[prop]);
        }
    return a;
};
var __spreadProps = function (a, b) { return __defProps(a, __getOwnPropDescs(b)); };
var __async = function (__this, __arguments, generator) {
    return new Promise(function (resolve, reject) {
        var fulfilled = function (value) {
            try {
                step(generator.next(value));
            }
            catch (e) {
                reject(e);
            }
        };
        var rejected = function (value) {
            try {
                step(generator.throw(value));
            }
            catch (e) {
                reject(e);
            }
        };
        var step = function (x) { return x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected); };
        step((generator = generator.apply(__this, __arguments)).next());
    });
};
var createDraftSafeSelector = function () {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    var selector = createSelector.apply(void 0, args);
    var wrappedSelector = function (value) {
        var rest = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            rest[_i - 1] = arguments[_i];
        }
        return selector.apply(void 0, __spreadArray([r(value) ? R(value) : value], rest));
    };
    return wrappedSelector;
};
// src/isPlainObject.ts
function isPlainObject(value) {
    if (typeof value !== "object" || value === null)
        return false;
    var proto = Object.getPrototypeOf(value);
    if (proto === null)
        return true;
    var baseProto = proto;
    while (Object.getPrototypeOf(baseProto) !== null) {
        baseProto = Object.getPrototypeOf(baseProto);
    }
    return proto === baseProto;
}
/** @class */ ((function (_super) {
    __extends(MiddlewareArray, _super);
    function MiddlewareArray() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var _this = _super.apply(this, args) || this;
        Object.setPrototypeOf(_this, MiddlewareArray.prototype);
        return _this;
    }
    Object.defineProperty(MiddlewareArray, Symbol.species, {
        get: function () {
            return MiddlewareArray;
        },
        enumerable: false,
        configurable: true
    });
    MiddlewareArray.prototype.concat = function () {
        var arr = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            arr[_i] = arguments[_i];
        }
        return _super.prototype.concat.apply(this, arr);
    };
    MiddlewareArray.prototype.prepend = function () {
        var arr = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            arr[_i] = arguments[_i];
        }
        if (arr.length === 1 && Array.isArray(arr[0])) {
            return new (MiddlewareArray.bind.apply(MiddlewareArray, __spreadArray([void 0], arr[0].concat(this))))();
        }
        return new (MiddlewareArray.bind.apply(MiddlewareArray, __spreadArray([void 0], arr.concat(this))))();
    };
    return MiddlewareArray;
})(Array));
function freezeDraftable(val) {
    return t(val) ? fn(val, function () {
    }) : val;
}
// src/immutableStateInvariantMiddleware.ts
process.env.NODE_ENV === "production";
// src/configureStore.ts
process.env.NODE_ENV === "production";
// src/createAction.ts
function createAction(type, prepareAction) {
    function actionCreator() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (prepareAction) {
            var prepared = prepareAction.apply(void 0, args);
            if (!prepared) {
                throw new Error("prepareAction did not return an object");
            }
            return __spreadValues(__spreadValues({
                type: type,
                payload: prepared.payload
            }, "meta" in prepared && { meta: prepared.meta }), "error" in prepared && { error: prepared.error });
        }
        return { type: type, payload: args[0] };
    }
    actionCreator.toString = function () { return "" + type; };
    actionCreator.type = type;
    actionCreator.match = function (action) { return action.type === type; };
    return actionCreator;
}
function isFSA(action) {
    return isPlainObject(action) && typeof action.type === "string" && Object.keys(action).every(isValidKey);
}
function isValidKey(key) {
    return ["type", "payload", "error", "meta"].indexOf(key) > -1;
}
// src/mapBuilders.ts
function executeReducerBuilderCallback(builderCallback) {
    var actionsMap = {};
    var actionMatchers = [];
    var defaultCaseReducer;
    var builder = {
        addCase: function (typeOrActionCreator, reducer) {
            if (process.env.NODE_ENV !== "production") {
                if (actionMatchers.length > 0) {
                    throw new Error("`builder.addCase` should only be called before calling `builder.addMatcher`");
                }
                if (defaultCaseReducer) {
                    throw new Error("`builder.addCase` should only be called before calling `builder.addDefaultCase`");
                }
            }
            var type = typeof typeOrActionCreator === "string" ? typeOrActionCreator : typeOrActionCreator.type;
            if (type in actionsMap) {
                throw new Error("addCase cannot be called with two reducers for the same action type");
            }
            actionsMap[type] = reducer;
            return builder;
        },
        addMatcher: function (matcher, reducer) {
            if (process.env.NODE_ENV !== "production") {
                if (defaultCaseReducer) {
                    throw new Error("`builder.addMatcher` should only be called before calling `builder.addDefaultCase`");
                }
            }
            actionMatchers.push({ matcher: matcher, reducer: reducer });
            return builder;
        },
        addDefaultCase: function (reducer) {
            if (process.env.NODE_ENV !== "production") {
                if (defaultCaseReducer) {
                    throw new Error("`builder.addDefaultCase` can only be called once");
                }
            }
            defaultCaseReducer = reducer;
            return builder;
        }
    };
    builderCallback(builder);
    return [actionsMap, actionMatchers, defaultCaseReducer];
}
// src/createReducer.ts
function isStateFunction(x) {
    return typeof x === "function";
}
var hasWarnedAboutObjectNotation = false;
function createReducer(initialState, mapOrBuilderCallback, actionMatchers, defaultCaseReducer) {
    if (actionMatchers === void 0) { actionMatchers = []; }
    if (process.env.NODE_ENV !== "production") {
        if (typeof mapOrBuilderCallback === "object") {
            if (!hasWarnedAboutObjectNotation) {
                hasWarnedAboutObjectNotation = true;
                console.warn("The object notation for `createReducer` is deprecated, and will be removed in RTK 2.0. Please use the 'builder callback' notation instead: https://redux-toolkit.js.org/api/createReducer");
            }
        }
    }
    var _c = typeof mapOrBuilderCallback === "function" ? executeReducerBuilderCallback(mapOrBuilderCallback) : [mapOrBuilderCallback, actionMatchers, defaultCaseReducer], actionsMap = _c[0], finalActionMatchers = _c[1], finalDefaultCaseReducer = _c[2];
    var getInitialState;
    if (isStateFunction(initialState)) {
        getInitialState = function () { return freezeDraftable(initialState()); };
    }
    else {
        var frozenInitialState_1 = freezeDraftable(initialState);
        getInitialState = function () { return frozenInitialState_1; };
    }
    function reducer(state, action) {
        if (state === void 0) { state = getInitialState(); }
        var caseReducers = __spreadArray([
            actionsMap[action.type]
        ], finalActionMatchers.filter(function (_c) {
            var matcher = _c.matcher;
            return matcher(action);
        }).map(function (_c) {
            var reducer2 = _c.reducer;
            return reducer2;
        }));
        if (caseReducers.filter(function (cr) { return !!cr; }).length === 0) {
            caseReducers = [finalDefaultCaseReducer];
        }
        return caseReducers.reduce(function (previousState, caseReducer) {
            if (caseReducer) {
                if (r(previousState)) {
                    var draft = previousState;
                    var result = caseReducer(draft, action);
                    if (result === void 0) {
                        return previousState;
                    }
                    return result;
                }
                else if (!t(previousState)) {
                    var result = caseReducer(previousState, action);
                    if (result === void 0) {
                        if (previousState === null) {
                            return previousState;
                        }
                        throw Error("A case reducer on a non-draftable value must not return undefined");
                    }
                    return result;
                }
                else {
                    return fn(previousState, function (draft) {
                        return caseReducer(draft, action);
                    });
                }
            }
            return previousState;
        }, state);
    }
    reducer.getInitialState = getInitialState;
    return reducer;
}
// src/createSlice.ts
var hasWarnedAboutObjectNotation2 = false;
function getType2(slice, actionKey) {
    return slice + "/" + actionKey;
}
function createSlice(options) {
    var name = options.name;
    if (!name) {
        throw new Error("`name` is a required option for createSlice");
    }
    if (typeof process !== "undefined" && process.env.NODE_ENV === "development") {
        if (options.initialState === void 0) {
            console.error("You must provide an `initialState` value that is not `undefined`. You may have misspelled `initialState`");
        }
    }
    var initialState = typeof options.initialState == "function" ? options.initialState : freezeDraftable(options.initialState);
    var reducers = options.reducers || {};
    var reducerNames = Object.keys(reducers);
    var sliceCaseReducersByName = {};
    var sliceCaseReducersByType = {};
    var actionCreators = {};
    reducerNames.forEach(function (reducerName) {
        var maybeReducerWithPrepare = reducers[reducerName];
        var type = getType2(name, reducerName);
        var caseReducer;
        var prepareCallback;
        if ("reducer" in maybeReducerWithPrepare) {
            caseReducer = maybeReducerWithPrepare.reducer;
            prepareCallback = maybeReducerWithPrepare.prepare;
        }
        else {
            caseReducer = maybeReducerWithPrepare;
        }
        sliceCaseReducersByName[reducerName] = caseReducer;
        sliceCaseReducersByType[type] = caseReducer;
        actionCreators[reducerName] = prepareCallback ? createAction(type, prepareCallback) : createAction(type);
    });
    function buildReducer() {
        if (process.env.NODE_ENV !== "production") {
            if (typeof options.extraReducers === "object") {
                if (!hasWarnedAboutObjectNotation2) {
                    hasWarnedAboutObjectNotation2 = true;
                    console.warn("The object notation for `createSlice.extraReducers` is deprecated, and will be removed in RTK 2.0. Please use the 'builder callback' notation instead: https://redux-toolkit.js.org/api/createSlice");
                }
            }
        }
        var _c = typeof options.extraReducers === "function" ? executeReducerBuilderCallback(options.extraReducers) : [options.extraReducers], _d = _c[0], extraReducers = _d === void 0 ? {} : _d, _e = _c[1], actionMatchers = _e === void 0 ? [] : _e, _f = _c[2], defaultCaseReducer = _f === void 0 ? void 0 : _f;
        var finalCaseReducers = __spreadValues(__spreadValues({}, extraReducers), sliceCaseReducersByType);
        return createReducer(initialState, function (builder) {
            for (var key in finalCaseReducers) {
                builder.addCase(key, finalCaseReducers[key]);
            }
            for (var _i = 0, actionMatchers_1 = actionMatchers; _i < actionMatchers_1.length; _i++) {
                var m = actionMatchers_1[_i];
                builder.addMatcher(m.matcher, m.reducer);
            }
            if (defaultCaseReducer) {
                builder.addDefaultCase(defaultCaseReducer);
            }
        });
    }
    var _reducer;
    return {
        name: name,
        reducer: function (state, action) {
            if (!_reducer)
                _reducer = buildReducer();
            return _reducer(state, action);
        },
        actions: actionCreators,
        caseReducers: sliceCaseReducersByName,
        getInitialState: function () {
            if (!_reducer)
                _reducer = buildReducer();
            return _reducer.getInitialState();
        }
    };
}
// src/entities/entity_state.ts
function getInitialEntityState() {
    return {
        ids: [],
        entities: {}
    };
}
function createInitialStateFactory() {
    function getInitialState(additionalState) {
        if (additionalState === void 0) { additionalState = {}; }
        return Object.assign(getInitialEntityState(), additionalState);
    }
    return { getInitialState: getInitialState };
}
// src/entities/state_selectors.ts
function createSelectorsFactory() {
    function getSelectors(selectState) {
        var selectIds = function (state) { return state.ids; };
        var selectEntities = function (state) { return state.entities; };
        var selectAll = createDraftSafeSelector(selectIds, selectEntities, function (ids, entities) { return ids.map(function (id) { return entities[id]; }); });
        var selectId = function (_, id) { return id; };
        var selectById = function (entities, id) { return entities[id]; };
        var selectTotal = createDraftSafeSelector(selectIds, function (ids) { return ids.length; });
        if (!selectState) {
            return {
                selectIds: selectIds,
                selectEntities: selectEntities,
                selectAll: selectAll,
                selectTotal: selectTotal,
                selectById: createDraftSafeSelector(selectEntities, selectId, selectById)
            };
        }
        var selectGlobalizedEntities = createDraftSafeSelector(selectState, selectEntities);
        return {
            selectIds: createDraftSafeSelector(selectState, selectIds),
            selectEntities: selectGlobalizedEntities,
            selectAll: createDraftSafeSelector(selectState, selectAll),
            selectTotal: createDraftSafeSelector(selectState, selectTotal),
            selectById: createDraftSafeSelector(selectGlobalizedEntities, selectId, selectById)
        };
    }
    return { getSelectors: getSelectors };
}
function createSingleArgumentStateOperator(mutator) {
    var operator = createStateOperator(function (_, state) { return mutator(state); });
    return function operation(state) {
        return operator(state, void 0);
    };
}
function createStateOperator(mutator) {
    return function operation(state, arg) {
        function isPayloadActionArgument(arg2) {
            return isFSA(arg2);
        }
        var runMutator = function (draft) {
            if (isPayloadActionArgument(arg)) {
                mutator(arg.payload, draft);
            }
            else {
                mutator(arg, draft);
            }
        };
        if (r(state)) {
            runMutator(state);
            return state;
        }
        else {
            return fn(state, runMutator);
        }
    };
}
// src/entities/utils.ts
function selectIdValue(entity, selectId) {
    var key = selectId(entity);
    if (process.env.NODE_ENV !== "production" && key === void 0) {
        console.warn("The entity passed to the `selectId` implementation returned undefined.", "You should probably provide your own `selectId` implementation.", "The entity that was passed:", entity, "The `selectId` implementation:", selectId.toString());
    }
    return key;
}
function ensureEntitiesArray(entities) {
    if (!Array.isArray(entities)) {
        entities = Object.values(entities);
    }
    return entities;
}
function splitAddedUpdatedEntities(newEntities, selectId, state) {
    newEntities = ensureEntitiesArray(newEntities);
    var added = [];
    var updated = [];
    for (var _i = 0, newEntities_1 = newEntities; _i < newEntities_1.length; _i++) {
        var entity = newEntities_1[_i];
        var id = selectIdValue(entity, selectId);
        if (id in state.entities) {
            updated.push({ id: id, changes: entity });
        }
        else {
            added.push(entity);
        }
    }
    return [added, updated];
}
// src/entities/unsorted_state_adapter.ts
function createUnsortedStateAdapter(selectId) {
    function addOneMutably(entity, state) {
        var key = selectIdValue(entity, selectId);
        if (key in state.entities) {
            return;
        }
        state.ids.push(key);
        state.entities[key] = entity;
    }
    function addManyMutably(newEntities, state) {
        newEntities = ensureEntitiesArray(newEntities);
        for (var _i = 0, newEntities_2 = newEntities; _i < newEntities_2.length; _i++) {
            var entity = newEntities_2[_i];
            addOneMutably(entity, state);
        }
    }
    function setOneMutably(entity, state) {
        var key = selectIdValue(entity, selectId);
        if (!(key in state.entities)) {
            state.ids.push(key);
        }
        state.entities[key] = entity;
    }
    function setManyMutably(newEntities, state) {
        newEntities = ensureEntitiesArray(newEntities);
        for (var _i = 0, newEntities_3 = newEntities; _i < newEntities_3.length; _i++) {
            var entity = newEntities_3[_i];
            setOneMutably(entity, state);
        }
    }
    function setAllMutably(newEntities, state) {
        newEntities = ensureEntitiesArray(newEntities);
        state.ids = [];
        state.entities = {};
        addManyMutably(newEntities, state);
    }
    function removeOneMutably(key, state) {
        return removeManyMutably([key], state);
    }
    function removeManyMutably(keys, state) {
        var didMutate = false;
        keys.forEach(function (key) {
            if (key in state.entities) {
                delete state.entities[key];
                didMutate = true;
            }
        });
        if (didMutate) {
            state.ids = state.ids.filter(function (id) { return id in state.entities; });
        }
    }
    function removeAllMutably(state) {
        Object.assign(state, {
            ids: [],
            entities: {}
        });
    }
    function takeNewKey(keys, update, state) {
        var original2 = state.entities[update.id];
        var updated = Object.assign({}, original2, update.changes);
        var newKey = selectIdValue(updated, selectId);
        var hasNewKey = newKey !== update.id;
        if (hasNewKey) {
            keys[update.id] = newKey;
            delete state.entities[update.id];
        }
        state.entities[newKey] = updated;
        return hasNewKey;
    }
    function updateOneMutably(update, state) {
        return updateManyMutably([update], state);
    }
    function updateManyMutably(updates, state) {
        var newKeys = {};
        var updatesPerEntity = {};
        updates.forEach(function (update) {
            if (update.id in state.entities) {
                updatesPerEntity[update.id] = {
                    id: update.id,
                    changes: __spreadValues(__spreadValues({}, updatesPerEntity[update.id] ? updatesPerEntity[update.id].changes : null), update.changes)
                };
            }
        });
        updates = Object.values(updatesPerEntity);
        var didMutateEntities = updates.length > 0;
        if (didMutateEntities) {
            var didMutateIds = updates.filter(function (update) { return takeNewKey(newKeys, update, state); }).length > 0;
            if (didMutateIds) {
                state.ids = Object.keys(state.entities);
            }
        }
    }
    function upsertOneMutably(entity, state) {
        return upsertManyMutably([entity], state);
    }
    function upsertManyMutably(newEntities, state) {
        var _c = splitAddedUpdatedEntities(newEntities, selectId, state), added = _c[0], updated = _c[1];
        updateManyMutably(updated, state);
        addManyMutably(added, state);
    }
    return {
        removeAll: createSingleArgumentStateOperator(removeAllMutably),
        addOne: createStateOperator(addOneMutably),
        addMany: createStateOperator(addManyMutably),
        setOne: createStateOperator(setOneMutably),
        setMany: createStateOperator(setManyMutably),
        setAll: createStateOperator(setAllMutably),
        updateOne: createStateOperator(updateOneMutably),
        updateMany: createStateOperator(updateManyMutably),
        upsertOne: createStateOperator(upsertOneMutably),
        upsertMany: createStateOperator(upsertManyMutably),
        removeOne: createStateOperator(removeOneMutably),
        removeMany: createStateOperator(removeManyMutably)
    };
}
// src/entities/sorted_state_adapter.ts
function createSortedStateAdapter(selectId, sort) {
    var _c = createUnsortedStateAdapter(selectId), removeOne = _c.removeOne, removeMany = _c.removeMany, removeAll = _c.removeAll;
    function addOneMutably(entity, state) {
        return addManyMutably([entity], state);
    }
    function addManyMutably(newEntities, state) {
        newEntities = ensureEntitiesArray(newEntities);
        var models = newEntities.filter(function (model) { return !(selectIdValue(model, selectId) in state.entities); });
        if (models.length !== 0) {
            merge(models, state);
        }
    }
    function setOneMutably(entity, state) {
        return setManyMutably([entity], state);
    }
    function setManyMutably(newEntities, state) {
        newEntities = ensureEntitiesArray(newEntities);
        if (newEntities.length !== 0) {
            merge(newEntities, state);
        }
    }
    function setAllMutably(newEntities, state) {
        newEntities = ensureEntitiesArray(newEntities);
        state.entities = {};
        state.ids = [];
        addManyMutably(newEntities, state);
    }
    function updateOneMutably(update, state) {
        return updateManyMutably([update], state);
    }
    function updateManyMutably(updates, state) {
        var appliedUpdates = false;
        for (var _i = 0, updates_1 = updates; _i < updates_1.length; _i++) {
            var update = updates_1[_i];
            var entity = state.entities[update.id];
            if (!entity) {
                continue;
            }
            appliedUpdates = true;
            Object.assign(entity, update.changes);
            var newId = selectId(entity);
            if (update.id !== newId) {
                delete state.entities[update.id];
                state.entities[newId] = entity;
            }
        }
        if (appliedUpdates) {
            resortEntities(state);
        }
    }
    function upsertOneMutably(entity, state) {
        return upsertManyMutably([entity], state);
    }
    function upsertManyMutably(newEntities, state) {
        var _c = splitAddedUpdatedEntities(newEntities, selectId, state), added = _c[0], updated = _c[1];
        updateManyMutably(updated, state);
        addManyMutably(added, state);
    }
    function areArraysEqual(a, b) {
        if (a.length !== b.length) {
            return false;
        }
        for (var i = 0; i < a.length && i < b.length; i++) {
            if (a[i] === b[i]) {
                continue;
            }
            return false;
        }
        return true;
    }
    function merge(models, state) {
        models.forEach(function (model) {
            state.entities[selectId(model)] = model;
        });
        resortEntities(state);
    }
    function resortEntities(state) {
        var allEntities = Object.values(state.entities);
        allEntities.sort(sort);
        var newSortedIds = allEntities.map(selectId);
        var ids = state.ids;
        if (!areArraysEqual(ids, newSortedIds)) {
            state.ids = newSortedIds;
        }
    }
    return {
        removeOne: removeOne,
        removeMany: removeMany,
        removeAll: removeAll,
        addOne: createStateOperator(addOneMutably),
        updateOne: createStateOperator(updateOneMutably),
        upsertOne: createStateOperator(upsertOneMutably),
        setOne: createStateOperator(setOneMutably),
        setMany: createStateOperator(setManyMutably),
        setAll: createStateOperator(setAllMutably),
        addMany: createStateOperator(addManyMutably),
        updateMany: createStateOperator(updateManyMutably),
        upsertMany: createStateOperator(upsertManyMutably)
    };
}
// src/entities/create_adapter.ts
function createEntityAdapter(options) {
    if (options === void 0) { options = {}; }
    var _c = __spreadValues({
        sortComparer: false,
        selectId: function (instance) { return instance.id; }
    }, options), selectId = _c.selectId, sortComparer = _c.sortComparer;
    var stateFactory = createInitialStateFactory();
    var selectorsFactory = createSelectorsFactory();
    var stateAdapter = sortComparer ? createSortedStateAdapter(selectId, sortComparer) : createUnsortedStateAdapter(selectId);
    return __spreadValues(__spreadValues(__spreadValues({
        selectId: selectId,
        sortComparer: sortComparer
    }, stateFactory), selectorsFactory), stateAdapter);
}
// src/nanoid.ts
var urlAlphabet = "ModuleSymbhasOwnPr-0123456789ABCDEFGHNRVfgctiUvz_KqYTJkLxpZXIjQW";
var nanoid = function (size) {
    if (size === void 0) { size = 21; }
    var id = "";
    var i = size;
    while (i--) {
        id += urlAlphabet[Math.random() * 64 | 0];
    }
    return id;
};
// src/createAsyncThunk.ts
var commonProperties = [
    "name",
    "message",
    "stack",
    "code"
];
var RejectWithValue = /** @class */ (function () {
    function RejectWithValue(payload, meta) {
        this.payload = payload;
        this.meta = meta;
    }
    return RejectWithValue;
}());
var FulfillWithMeta = /** @class */ (function () {
    function FulfillWithMeta(payload, meta) {
        this.payload = payload;
        this.meta = meta;
    }
    return FulfillWithMeta;
}());
var miniSerializeError = function (value) {
    if (typeof value === "object" && value !== null) {
        var simpleError = {};
        for (var _i = 0, commonProperties_1 = commonProperties; _i < commonProperties_1.length; _i++) {
            var property = commonProperties_1[_i];
            if (typeof value[property] === "string") {
                simpleError[property] = value[property];
            }
        }
        return simpleError;
    }
    return { message: String(value) };
};
((function () {
    function createAsyncThunk2(typePrefix, payloadCreator, options) {
        var fulfilled = createAction(typePrefix + "/fulfilled", function (payload, requestId, arg, meta) { return ({
            payload: payload,
            meta: __spreadProps(__spreadValues({}, meta || {}), {
                arg: arg,
                requestId: requestId,
                requestStatus: "fulfilled"
            })
        }); });
        var pending = createAction(typePrefix + "/pending", function (requestId, arg, meta) { return ({
            payload: void 0,
            meta: __spreadProps(__spreadValues({}, meta || {}), {
                arg: arg,
                requestId: requestId,
                requestStatus: "pending"
            })
        }); });
        var rejected = createAction(typePrefix + "/rejected", function (error, requestId, arg, payload, meta) { return ({
            payload: payload,
            error: (options && options.serializeError || miniSerializeError)(error || "Rejected"),
            meta: __spreadProps(__spreadValues({}, meta || {}), {
                arg: arg,
                requestId: requestId,
                rejectedWithValue: !!payload,
                requestStatus: "rejected",
                aborted: (error == null ? void 0 : error.name) === "AbortError",
                condition: (error == null ? void 0 : error.name) === "ConditionError"
            })
        }); });
        var displayedWarning = false;
        var AC = typeof AbortController !== "undefined" ? AbortController : /** @class */ (function () {
            function class_1() {
                this.signal = {
                    aborted: false,
                    addEventListener: function () {
                    },
                    dispatchEvent: function () {
                        return false;
                    },
                    onabort: function () {
                    },
                    removeEventListener: function () {
                    },
                    reason: void 0,
                    throwIfAborted: function () {
                    }
                };
            }
            class_1.prototype.abort = function () {
                if (process.env.NODE_ENV !== "production") {
                    if (!displayedWarning) {
                        displayedWarning = true;
                        console.info("This platform does not implement AbortController. \nIf you want to use the AbortController to react to `abort` events, please consider importing a polyfill like 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only'.");
                    }
                }
            };
            return class_1;
        }());
        function actionCreator(arg) {
            return function (dispatch, getState, extra) {
                var requestId = (options == null ? void 0 : options.idGenerator) ? options.idGenerator(arg) : nanoid();
                var abortController = new AC();
                var abortReason;
                function abort(reason) {
                    abortReason = reason;
                    abortController.abort();
                }
                var promise2 = function () {
                    return __async(this, null, function () {
                        var _a, _b, finalAction, conditionResult, abortedPromise, err_1, skipDispatch;
                        return __generator(this, function (_c) {
                            switch (_c.label) {
                                case 0:
                                    _c.trys.push([0, 4, , 5]);
                                    conditionResult = (_a = options == null ? void 0 : options.condition) == null ? void 0 : _a.call(options, arg, { getState: getState, extra: extra });
                                    if (!isThenable(conditionResult)) return [3 /*break*/, 2];
                                    return [4 /*yield*/, conditionResult];
                                case 1:
                                    conditionResult = _c.sent();
                                    _c.label = 2;
                                case 2:
                                    if (conditionResult === false || abortController.signal.aborted) {
                                        throw {
                                            name: "ConditionError",
                                            message: "Aborted due to condition callback returning false."
                                        };
                                    }
                                    abortedPromise = new Promise(function (_, reject) { return abortController.signal.addEventListener("abort", function () { return reject({
                                        name: "AbortError",
                                        message: abortReason || "Aborted"
                                    }); }); });
                                    dispatch(pending(requestId, arg, (_b = options == null ? void 0 : options.getPendingMeta) == null ? void 0 : _b.call(options, { requestId: requestId, arg: arg }, { getState: getState, extra: extra })));
                                    return [4 /*yield*/, Promise.race([
                                            abortedPromise,
                                            Promise.resolve(payloadCreator(arg, {
                                                dispatch: dispatch,
                                                getState: getState,
                                                extra: extra,
                                                requestId: requestId,
                                                signal: abortController.signal,
                                                abort: abort,
                                                rejectWithValue: function (value, meta) {
                                                    return new RejectWithValue(value, meta);
                                                },
                                                fulfillWithValue: function (value, meta) {
                                                    return new FulfillWithMeta(value, meta);
                                                }
                                            })).then(function (result) {
                                                if (result instanceof RejectWithValue) {
                                                    throw result;
                                                }
                                                if (result instanceof FulfillWithMeta) {
                                                    return fulfilled(result.payload, requestId, arg, result.meta);
                                                }
                                                return fulfilled(result, requestId, arg);
                                            })
                                        ])];
                                case 3:
                                    finalAction = _c.sent();
                                    return [3 /*break*/, 5];
                                case 4:
                                    err_1 = _c.sent();
                                    finalAction = err_1 instanceof RejectWithValue ? rejected(null, requestId, arg, err_1.payload, err_1.meta) : rejected(err_1, requestId, arg);
                                    return [3 /*break*/, 5];
                                case 5:
                                    skipDispatch = options && !options.dispatchConditionRejection && rejected.match(finalAction) && finalAction.meta.condition;
                                    if (!skipDispatch) {
                                        dispatch(finalAction);
                                    }
                                    return [2 /*return*/, finalAction];
                            }
                        });
                    });
                }();
                return Object.assign(promise2, {
                    abort: abort,
                    requestId: requestId,
                    arg: arg,
                    unwrap: function () {
                        return promise2.then(unwrapResult);
                    }
                });
            };
        }
        return Object.assign(actionCreator, {
            pending: pending,
            rejected: rejected,
            fulfilled: fulfilled,
            typePrefix: typePrefix
        });
    }
    createAsyncThunk2.withTypes = function () { return createAsyncThunk2; };
    return createAsyncThunk2;
}))();
function unwrapResult(action) {
    if (action.meta && action.meta.rejectedWithValue) {
        throw action.payload;
    }
    if (action.error) {
        throw action.error;
    }
    return action.payload;
}
function isThenable(value) {
    return value !== null && typeof value === "object" && typeof value.then === "function";
}
var alm = "listenerMiddleware";
createAction(alm + "/add");
createAction(alm + "/removeAll");
createAction(alm + "/remove");
var promise;
typeof queueMicrotask === "function" ? queueMicrotask.bind(typeof window !== "undefined" ? window : typeof global !== "undefined" ? global : globalThis) : function (cb) { return (promise || (promise = Promise.resolve())).then(cb).catch(function (err) { return setTimeout(function () {
    throw err;
}, 0); }); };
// src/index.ts
F();

var _a$1;
var SpeakerState;
(function (SpeakerState) {
    SpeakerState["Active"] = "active";
    SpeakerState["Inactive"] = "inactive";
    SpeakerState["Transitioning"] = "transitioning";
})(SpeakerState || (SpeakerState = {}));
var remainingParticipantsAdapter = createEntityAdapter({
    selectId: function (participant) { return participant; }
});
var historyParticipantsAdapter = createEntityAdapter({
    selectId: function (participant) { return participant; }
});
function getInitialState() {
    var initialState = {
        active: false,
        selectionStrategy: AutomodSelectionStrategy.Playlist,
        history: historyParticipantsAdapter.getInitialState(),
        remaining: remainingParticipantsAdapter.getInitialState(),
        animationOnRandom: false,
        allowDoubleSelection: false,
        timeLimit: null,
        showList: false,
        considerHandRaise: false,
        speakerId: undefined,
        issuedBy: undefined,
        speakerState: SpeakerState.Inactive
    };
    return initialState;
}
function reset(state) {
    var initialState = getInitialState();
    state.active = initialState.active;
    state.history = historyParticipantsAdapter.getInitialState();
    state.remaining = remainingParticipantsAdapter.getInitialState();
    state.selectionStrategy = initialState.selectionStrategy;
    state.animationOnRandom = initialState.animationOnRandom;
    state.allowDoubleSelection = initialState.allowDoubleSelection;
    state.timeLimit = initialState.timeLimit;
    state.showList = initialState.showList;
    state.considerHandRaise = initialState.considerHandRaise;
    state.speakerId = undefined;
    state.issuedBy = undefined;
    state.speakerState = SpeakerState.Inactive;
}
var automodSlice = createSlice({
    name: "automod",
    initialState: getInitialState(),
    reducers: {
        started: function (state, _a) {
            var payload = _a.payload;
            state.active = true;
            historyParticipantsAdapter.setAll(state.history, payload.history);
            remainingParticipantsAdapter.setAll(state.remaining, payload.remaining);
            state.selectionStrategy = payload.selectionStrategy;
            state.animationOnRandom = payload.animationOnRandom;
            state.allowDoubleSelection = payload.allowDoubleSelection;
            state.timeLimit = payload.timeLimit || null;
            state.showList = payload.showList;
            state.considerHandRaise = payload.considerHandRaise;
            state.issuedBy = payload.issuedBy;
        },
        stopped: reset,
        speakerUpdated: function (state, action) {
            state.speakerId = action.payload.speaker || undefined;
            remainingParticipantsAdapter.setAll(state.remaining, action.payload.remaining || []);
            historyParticipantsAdapter.setAll(state.history, action.payload.history || []);
            // state.animationRunning = false;
        },
        remainingUpdated: function (state, action) {
            remainingParticipantsAdapter.setAll(state.remaining, action.payload.remaining);
        },
        setAsActiveSpeaker: function (state) {
            state.speakerState = SpeakerState.Active;
        },
        setAsInactiveSpeaker: function (state) {
            state.speakerState = SpeakerState.Inactive;
        },
        setAsTransitioningSpeaker: function (state) {
            state.speakerState = SpeakerState.Transitioning;
        }
    },
    extraReducers: function (builder) {
        builder.addCase(joinSuccess, function (state, _a) {
            var _b = _a.payload, automod = _b.automod, participantId = _b.participantId;
            state.participantId = participantId;
            if (automod) {
                state.active = true;
                state.history = historyParticipantsAdapter.setAll(state.history, automod.config.history);
                state.remaining = remainingParticipantsAdapter.setAll(state.remaining, automod.config.remaining);
                state.selectionStrategy = automod.config.selectionStrategy;
                state.animationOnRandom = automod.config.animationOnRandom;
                state.allowDoubleSelection = automod.config.allowDoubleSelection;
                state.timeLimit = automod.config.timeLimit || null;
                state.showList = automod.config.showList;
                state.considerHandRaise = automod.config.considerHandRaise;
                state.speakerId = automod.speaker || undefined;
            }
            else {
                reset(state);
            }
        });
    }
});
var started$1 = (_a$1 = automodSlice.actions, _a$1.started), stopped$1 = _a$1.stopped, speakerUpdated = _a$1.speakerUpdated, remainingUpdated = _a$1.remainingUpdated, setAsActiveSpeaker = _a$1.setAsActiveSpeaker, setAsInactiveSpeaker = _a$1.setAsInactiveSpeaker, setAsTransitioningSpeaker = _a$1.setAsTransitioningSpeaker;
var actions$1 = automodSlice.actions;
var selectAutomodActiveState = function (state) {
    return state.automod.active;
};
var historySelectors = historyParticipantsAdapter.getSelectors();
var remainingSelectors = remainingParticipantsAdapter.getSelectors();
var selectAllHistory = function (state) {
    return historySelectors.selectAll(state.automod.history);
};
var selectAllRemaining = function (state) {
    return remainingSelectors.selectAll(state.automod.remaining);
};
var selectAutomoderationParticipantIds = createSelector(selectAllHistory, selectAllRemaining, function (history, remaining) {
    return history.concat(remaining);
});
var selectSpeakerId = function (state) {
    return state.automod.speakerId;
};
var selectSpeakerState = function (state) {
    return state.automod.speakerState;
};
var selectMyParticipantId = function (state) {
    return state.automod.participantId;
};
var automodReducer = automodSlice.reducer;

var automodSlice$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    actions: actions$1,
    automodReducer: automodReducer,
    automodSlice: automodSlice,
    remainingUpdated: remainingUpdated,
    selectAutomodActiveState: selectAutomodActiveState,
    selectAutomoderationParticipantIds: selectAutomoderationParticipantIds,
    selectMyParticipantId: selectMyParticipantId,
    selectSpeakerId: selectSpeakerId,
    selectSpeakerState: selectSpeakerState,
    setAsActiveSpeaker: setAsActiveSpeaker,
    setAsInactiveSpeaker: setAsInactiveSpeaker,
    setAsTransitioningSpeaker: setAsTransitioningSpeaker,
    speakerUpdated: speakerUpdated,
    started: started$1,
    stopped: stopped$1
});

var StyledList = styled(List$1)(function (_a) {
    var theme = _a.theme;
    return ({
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        overflow: "auto"
    });
});
var TalkingStickParticipantList = function (_a) {
    var _b = _a.participants, participants = _b === void 0 ? [] : _b;
    var activeSpeakerId = useSelector(selectSpeakerId);
    var myParticipantId = useSelector(selectMyParticipantId);
    var renderParticipantListItems = function () {
        return participants.map(function (participant) { return (React.createElement(ParticipantListItem$1, __assign({ key: participant.id }, participant, { isActiveSpeaker: participant.id === activeSpeakerId, isCurrentUser: participant.id === myParticipantId }))); });
    };
    return React.createElement(StyledList, null, renderParticipantListItems());
};

var TalkingStickTabPanel = function () {
    var dispatch = useDispatch();
    var _a = useTranslation(), t = _a.t, i18n = _a.i18n;
    var _b = useAutomodContext(), configurationParticipants = _b.configurationParticipants, apiMessages = _b.apiMessages;
    var isAutomodActive = useSelector(selectAutomodActiveState);
    var runningParticipantIds = useSelector(selectAutomoderationParticipantIds);
    var participantsWithoutUser = configurationParticipants.slice(1);
    var userInitiatingTalkingStick = configurationParticipants[0];
    var activeParticipants = useMemo(function () {
        var map = new Map();
        for (var _i = 0, configurationParticipants_1 = configurationParticipants; _i < configurationParticipants_1.length; _i++) {
            var participant = configurationParticipants_1[_i];
            map.set(participant.id, participant);
        }
        return runningParticipantIds.map(function (id) { return map.get(id); }).filter(Boolean);
    }, [configurationParticipants, runningParticipantIds]);
    var _c = useState(SortOption.NameASC), selectedSortType = _c[0], setSelectedSortType = _c[1];
    var sortParticipants = useCallback(sortParticipantsWithConfig({ language: i18n.language }), [i18n.language]);
    var sortedParticipants = useMemo(function () {
        return sortParticipants(participantsWithoutUser, selectedSortType);
    }, [participantsWithoutUser, sortParticipants, selectedSortType]);
    var handleStart = useCallback(function () {
        var participantIdList = sortedParticipants.map(function (participant) { return participant.id; });
        dispatch(apiMessages === null || apiMessages === void 0 ? void 0 : apiMessages.talkingStickStart.action({
            playlist: __spreadArray$1([userInitiatingTalkingStick.id], participantIdList, true)
        }));
    }, [sortedParticipants, apiMessages]);
    var handleStop = function () {
        dispatch(apiMessages === null || apiMessages === void 0 ? void 0 : apiMessages.stop.action());
    };
    var handleSkipSpeaker = function () {
        dispatch(apiMessages === null || apiMessages === void 0 ? void 0 : apiMessages.selectNext.action());
    };
    return (React.createElement(Stack, { spacing: 2, flex: 1, overflow: "hidden" },
        !isAutomodActive && (React.createElement(Box, { display: "flex", justifyContent: "space-between", alignItems: "center" },
            React.createElement(Typography, null, t("sort-label")),
            React.createElement(SortField$1, { key: selectedSortType, selectedSortType: selectedSortType, onChange: setSelectedSortType }))),
        React.createElement(Stack, { overflow: "hidden", flex: 1 },
            React.createElement(TalkingStickParticipantList, { participants: isAutomodActive ? activeParticipants : sortedParticipants })),
        React.createElement(Stack, null, !isAutomodActive ? (React.createElement(Button, { type: "button", onClick: handleStart }, t("global-start-now"))) : (React.createElement(Stack, { flexDirection: "column", spacing: 1 },
            React.createElement(Button, { type: "button", onClick: handleSkipSpeaker }, t("talking-stick-skip-speaker")),
            React.createElement(Button, { type: "button", color: "secondary", onClick: handleStop }, t("global-stop")))))));
};
var TalkingStickTabPanel$1 = memo(TalkingStickTabPanel);

var createTalkingStickNotification = function (_a) {
    var localMediaContext = _a.localMediaContext, currentId = _a.currentId, unmutedId = _a.unmutedId, passTalkingStick = _a.passTalkingStick, lastSpeaker = _a.lastSpeaker, isUnmuted = _a.isUnmuted, propId = _a.id;
    var id = propId || generateUniquedId();
    if (isUnmuted) {
        return (React.createElement(UnmutedNotificationMessage, { id: id, onNext: function () {
                passTalkingStick();
                notifications.close(unmutedId);
            }, lastSpeaker: lastSpeaker }));
    }
    return (React.createElement(MutedNotificationMessage, { id: id, onUnmute: function () {
            localMediaContext.reconfigure({ audio: true });
            notifications.close(currentId);
            notificationAction({
                key: unmutedId,
                persist: true,
                variant: "success",
                hideCloseButton: true,
                msg: (React.createElement(UnmutedNotificationMessage, { id: id, onNext: function () {
                        passTalkingStick();
                        notifications.close(unmutedId);
                    }, lastSpeaker: lastSpeaker })),
                SnackbarProps: {
                    role: "alert",
                    "aria-labelledby": "".concat(id, "-title")
                }
            });
        }, onNext: function () {
            passTalkingStick();
        } }));
};
var MutedNotificationMessage = function (props) {
    var t = useTranslation().t;
    return (React.createElement(TrapFocus, { open: true },
        React.createElement(Stack, { spacing: 1 },
            React.createElement("h3", { id: "".concat(props.id, "-title") }, t("talking-stick-speaker-announcement")),
            React.createElement(Box, { display: "flex", gap: 1 },
                React.createElement(Button, { onClick: props.onUnmute, variant: "contained", color: "primary", fullWidth: true }, t("talking-stick-notification-unmute")),
                React.createElement(Button, { onClick: props.onNext, variant: "contained", color: "secondary", fullWidth: true }, t("talking-stick-notification-next-speaker"))))));
};
var UnmutedNotificationMessage = function (props) {
    var t = useTranslation().t;
    return (React.createElement(TrapFocus, { open: true },
        React.createElement(Stack, { spacing: 1 },
            React.createElement("h3", { id: "".concat(props.id, "-title") }, t(props.lastSpeaker
                ? "talking-stick-unmuted-notification-last-participant"
                : "talking-stick-unmuted-notification")),
            React.createElement(Box, { display: "flex", justifyContent: "flex-end" },
                React.createElement(Button, { onClick: props.onNext, variant: "contained", color: "primary" }, t("talking-stick-notification-next-speaker"))))));
};

var AutomodError;
(function (AutomodError) {
    AutomodError["VoteAlreadyActive"] = "vote_already_active";
    AutomodError["NoVoteActive"] = "no_vote_active";
    AutomodError["InvalidVoteId"] = "invalid_vote_id";
    AutomodError["Ineligible"] = "ineligible";
    AutomodError["Internal"] = "internal";
})(AutomodError || (AutomodError = {}));
var isError = isEnumErrorStruct(AutomodError);

var AutomodSelectHow;
(function (AutomodSelectHow) {
    AutomodSelectHow["None"] = "none";
    AutomodSelectHow["Random"] = "random";
    AutomodSelectHow["Specific"] = "specific";
    AutomodSelectHow["Next"] = "next";
})(AutomodSelectHow || (AutomodSelectHow = {}));
var start$1 = createSignalingApiCall("automod", "start");
var stop$1 = createSignalingApiCall("automod", "stop");
var edit = createSignalingApiCall("automod", "edit");
var select = createSignalingApiCall("automod", "select");
var pass = createSignalingApiCall("automod", "yield");
var talkingStickStart = {
    action: function (payload) {
        return start$1.action({
            selectionStrategy: AutomodSelectionStrategy.Playlist,
            showList: true,
            considerHandRaise: false,
            allowDoubleSelection: false,
            animationOnRandom: false,
            autoAppendOnJoin: true,
            playlist: payload.playlist
        });
    }
};
var selectNext = {
    action: function () {
        return select.action({ how: AutomodSelectHow.Next });
    }
};
var selectNone = {
    action: function () {
        return select.action({ how: AutomodSelectHow.None });
    }
};
var selectSpecific = {
    action: function (participant, keepInRemaining) {
        return select.action({
            how: AutomodSelectHow.Specific,
            participant: participant,
            keepInRemaining: keepInRemaining
        });
    }
};
var selectRandom = {
    action: function () {
        return select.action({ how: AutomodSelectHow.Random });
    }
};
var createOutgoingAutomod = function (createModule, sendMessage) {
    var handler = createModule(function (builder) {
        builder.addCase(start$1.action, function (_state, action) {
            sendMessage(start$1(action.payload));
        });
        builder.addCase(edit.action, function (_state, action) {
            sendMessage(edit(action.payload));
        });
        builder.addCase(select.action, function (_state, action) {
            sendMessage(select(action.payload));
        });
        builder.addCase(pass.action, function (_state, action) {
            sendMessage(pass(action.payload));
        });
        builder.addCase(stop$1.action, function (_state, action) {
            sendMessage(stop$1(action.payload));
        });
    });
    return {
        actions: {
            stop: stop$1,
            edit: edit,
            select: select,
            pass: pass,
            talkingStickStart: talkingStickStart,
            selectNext: selectNext,
            selectNone: selectNone,
            selectSpecific: selectSpecific,
            selectRandom: selectRandom
        },
        handler: handler
    };
};

var LegalVoteContext = createContext(null);
var LegalVodeProviderContext = function (_a) {
    var votingUsers = _a.votingUsers, apiMessages = _a.apiMessages, children = _a.children;
    return (React.createElement(LegalVoteContext.Provider, { value: {
            votingUsers: votingUsers,
            apiMessages: apiMessages
        } }, children));
};

var VotedFailedReason;
(function (VotedFailedReason) {
    VotedFailedReason["InvalidVoteId"] = "invalid_vote_id";
    VotedFailedReason["Ineligible"] = "ineligible";
    VotedFailedReason["InvalidOption"] = "invalid_option";
})(VotedFailedReason || (VotedFailedReason = {}));
var StopKind;
(function (StopKind) {
    StopKind["ByParticipant"] = "by_participant";
    StopKind["Expired"] = "expired";
    StopKind["Auto"] = "auto";
})(StopKind || (StopKind = {}));
var VoteFinalResults;
(function (VoteFinalResults) {
    VoteFinalResults["Valid"] = "valid";
    VoteFinalResults["Invalid"] = "invalid";
})(VoteFinalResults || (VoteFinalResults = {}));
var VoteInvalidReason;
(function (VoteInvalidReason) {
    VoteInvalidReason["AbstainDisabled"] = "abstain_disabled";
    VoteInvalidReason["VoteCountInconsistent"] = "vote_count_inconsistent";
})(VoteInvalidReason || (VoteInvalidReason = {}));
var VoteCancelReasonType;
(function (VoteCancelReasonType) {
    VoteCancelReasonType["InitiatorLeft"] = "initiator_left";
    VoteCancelReasonType["RoomDestroyed"] = "room_destroyed";
    VoteCancelReasonType["Custom"] = "custom";
})(VoteCancelReasonType || (VoteCancelReasonType = {}));
var LegalVoteError;
(function (LegalVoteError) {
    LegalVoteError["VoteAlreadyActive"] = "vote_already_active";
    LegalVoteError["NoVoteActive"] = "no_vote_active";
    LegalVoteError["InvalidVoteId"] = "invalid_vote_id";
    LegalVoteError["Ineligible"] = "ineligible";
    LegalVoteError["Internal"] = "internal";
})(LegalVoteError || (LegalVoteError = {}));

var _a;
var cancelReasonFromApiType = function (cancel) {
    if (cancel.reason === VoteCancelReasonType.Custom) {
        return [VoteCancelReasonType.Custom, cancel.custom];
    }
    else {
        if (cancel.reason === VoteCancelReasonType.InitiatorLeft) {
            return [VoteCancelReasonType.InitiatorLeft, undefined];
        }
        else if (cancel.reason === VoteCancelReasonType.RoomDestroyed) {
            return [VoteCancelReasonType.RoomDestroyed, undefined];
        }
        else {
            console.error(new Error("Invalid Cancel Reason from legal-vote Cancel"));
            return;
        }
    }
};
var LegalVoteStates;
(function (LegalVoteStates) {
    LegalVoteStates["Active"] = "active";
    LegalVoteStates["Finished"] = "finished";
    LegalVoteStates["Canceled"] = "canceled";
})(LegalVoteStates || (LegalVoteStates = {}));
var newLegalVoteFromApiType = function (_a) {
    var legalVoteId = _a.legalVoteId, other = __rest(_a, ["legalVoteId"]);
    return (__assign(__assign({ id: legalVoteId }, other), { localStartTime: new Date().toISOString(), votes: { yes: 0, no: 0, abstain: 0 }, state: LegalVoteStates.Active, votedAt: null }));
};
var legalVoteAdapter = createEntityAdapter({
    sortComparer: function (a, b) {
        var aDate = Date.parse(a.startTime);
        var bDate = Date.parse(b.startTime);
        return aDate - bDate;
    }
});
var initialState = {
    votes: legalVoteAdapter.getInitialState(),
    savedLegalVotes: [],
    showResultWindow: false
};
// We currently only allow a single active shown vote.
// It can be changed to Array<VoteId> once we decided to support multiple active votes.
var legalVoteSlice = createSlice({
    name: "legalVote",
    initialState: initialState,
    reducers: {
        initialize: function (state) {
            state.votes = legalVoteAdapter.getInitialState();
            state.savedLegalVotes = [];
            state.showResultWindow = false;
        },
        started: function (state, _a) {
            var payload = _a.payload;
            state.currentShownVote = payload.legalVoteId;
            state.activeVote = payload.legalVoteId;
            state.showResultWindow = true;
            var vote = newLegalVoteFromApiType(payload);
            legalVoteAdapter.addOne(state.votes, vote);
        },
        stopped: function (state, _a) {
            var payload = _a.payload;
            state.activeVote = undefined;
            legalVoteAdapter.updateOne(state.votes, {
                id: payload.legalVoteId,
                changes: {
                    localStopTime: new Date().toISOString(),
                    state: LegalVoteStates.Finished,
                    votes: {
                        yes: payload.results === "valid" ? payload.yes : 0,
                        no: payload.results === "valid" ? payload.no : 0,
                        abstain: payload.results === "valid" ? payload.abstain || 0 : 0
                    },
                    votingRecord: payload.results === "valid" ? payload.votingRecord : {}
                }
            });
        },
        updated: function (state, _a) {
            var payload = _a.payload;
            // Add 0 default for abstain
            var votes = {
                yes: payload.yes,
                no: payload.no,
                abstain: payload.abstain || 0
            };
            legalVoteAdapter.updateOne(state.votes, {
                id: payload.legalVoteId,
                changes: { votes: votes, votingRecord: payload.votingRecord || undefined }
            });
        },
        canceled: function (state, _a) {
            var payload = _a.payload;
            var convertedType = cancelReasonFromApiType(payload);
            if (convertedType !== undefined) {
                state.activeVote = undefined;
                var cancelReason = convertedType[0], customCancelReason = convertedType[1];
                legalVoteAdapter.updateOne(state.votes, {
                    id: payload.legalVoteId,
                    changes: {
                        state: LegalVoteStates.Canceled,
                        cancelReason: cancelReason,
                        customCancelReason: customCancelReason
                    }
                });
            }
        },
        voted: function (state, _a) {
            var payload = _a.payload;
            legalVoteAdapter.updateOne(state.votes, {
                id: payload.legalVoteId,
                changes: { votedAt: new Date(Date.now()).toISOString() }
            });
        },
        closed: function (state, _a) {
            var payload = _a.payload;
            if (state.activeVote !== undefined) {
                // We only allow closing already finished votes for now
                return;
            }
            if (state.currentShownVote === payload) {
                state.currentShownVote = undefined;
            }
        },
        // tmp action until UX design is done
        closeResultWindow: function (state) {
            state.showResultWindow = false;
        },
        saveLegalVoteFormValues: function (state, _a) {
            var payload = _a.payload;
            var index = state.savedLegalVotes.findIndex(function (savedLegalVote) { return savedLegalVote.id === payload.id; });
            if (index !== -1) {
                state.savedLegalVotes[index] = __assign(__assign({}, state.savedLegalVotes[index]), payload);
                return;
            }
            state.savedLegalVotes.push(__assign({ id: state.savedLegalVotes.length }, payload));
        },
        saveSelectedOption: function (state, _a) {
            var payload = _a.payload;
            legalVoteAdapter.updateOne(state.votes, {
                id: payload.legalVoteId,
                changes: { selectedOption: payload.selectedOption }
            });
        }
    }
});
var initialize = (_a = legalVoteSlice.actions, _a.initialize), started = _a.started, stopped = _a.stopped, updated = _a.updated, voted = _a.voted, canceled = _a.canceled, closed = _a.closed, closeResultWindow = _a.closeResultWindow, saveLegalVoteFormValues = _a.saveLegalVoteFormValues, saveSelectedOption = _a.saveSelectedOption;
var actions = legalVoteSlice.actions;
var voteSelectors = legalVoteAdapter.getSelectors(function (state) { return state.legalVote.votes; });
var selectVoteById = function (id) { return function (state) {
    return voteSelectors.selectById(state, id);
}; };
var selectVoteIds = function (state) {
    return voteSelectors.selectIds(state);
};
var selectAllVotes = function (state) {
    return voteSelectors.selectAll(state);
};
var selectVotes = function (state) {
    return voteSelectors.selectEntities(state);
};
var selectShowLegalVoteWindow = function (state) {
    return state.legalVote.showResultWindow;
};
var selectCurrentShownVoteId = function (state) {
    return state.legalVote.currentShownVote;
};
var selectCurrentShownVote = function (state) {
    return state.legalVote.currentShownVote
        ? selectVoteById(state.legalVote.currentShownVote)(state)
        : undefined;
};
var selectActiveVoteId = function (state) {
    return state.legalVote.activeVote;
};
var selectAllSavedLegalVotes = function (state) {
    return state.legalVote.savedLegalVotes;
};
var selectSavedLegalVotePerId = function (id) { return function (state) {
    return state.legalVote.savedLegalVotes.find(function (legalVote) { return legalVote.id === id; });
}; };
var selectLegalVoteId = function (state) {
    return state.legalVote.savedLegalVotes.length;
};
var legalVoteReducer = legalVoteSlice.reducer;

var legalVoteSlice$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    actions: actions,
    canceled: canceled,
    closeResultWindow: closeResultWindow,
    closed: closed,
    initialize: initialize,
    legalVoteReducer: legalVoteReducer,
    legalVoteSlice: legalVoteSlice,
    saveLegalVoteFormValues: saveLegalVoteFormValues,
    saveSelectedOption: saveSelectedOption,
    selectActiveVoteId: selectActiveVoteId,
    selectAllSavedLegalVotes: selectAllSavedLegalVotes,
    selectAllVotes: selectAllVotes,
    selectCurrentShownVote: selectCurrentShownVote,
    selectCurrentShownVoteId: selectCurrentShownVoteId,
    selectLegalVoteId: selectLegalVoteId,
    selectSavedLegalVotePerId: selectSavedLegalVotePerId,
    selectShowLegalVoteWindow: selectShowLegalVoteWindow,
    selectVoteById: selectVoteById,
    selectVoteIds: selectVoteIds,
    selectVotes: selectVotes,
    started: started,
    stopped: stopped,
    updated: updated,
    voted: voted
});

var ListItem = styled(ListItem$1)(function (_a) {
    var theme = _a.theme;
    return ({
        padding: theme.spacing(1, 0, 1, 0),
        cursor: "pointer",
        "& .more-icon": {
            color: "transparent"
        },
        ":hover": {
            opacity: 0.8,
            "& .more-icon": {
                color: theme.palette.primary.contrastText
            }
        }
    });
});
var CustomList = styled(List$1)({
    flex: "1 1 auto",
    overflowY: "auto",
    textAlign: "left",
    width: "100%"
});
var ListItemAvatar = styled(ListItemAvatar$1)({
    minWidth: "initial"
});
var isSelectedParticipant = function (participants, id) { var _a; return Boolean((_a = find(participants, { id: id })) === null || _a === void 0 ? void 0 : _a.isSelected); };
var getSelectedParticipants = function (participants) {
    return participants.filter(function (participant) { return participant.isSelected; });
};
var ParticipantSelector = function (_a) {
    var name = _a.name;
    var t = useTranslation().t;
    var legalVoteHandle = useContext(LegalVoteContext);
    var pantansWithoutGuestAndSip = (legalVoteHandle === null || legalVoteHandle === void 0 ? void 0 : legalVoteHandle.votingUsers) || [];
    var _b = useFormikContext(), setFieldValue = _b.setFieldValue, errors = _b.errors;
    var error = get(errors, name, "");
    var hasError = Boolean(error);
    var _c = useState([]), allowedParticipants = _c[0], setAlloedParticipants = _c[1];
    useEffect(function () {
        setAlloedParticipants(function (allowedParticipants) {
            return pantansWithoutGuestAndSip.map(function (_a) {
                var displayName = _a.displayName, avatarUrl = _a.avatarUrl, id = _a.id;
                return ({
                    displayName: displayName,
                    avatarUrl: avatarUrl,
                    id: id,
                    isSelected: isSelectedParticipant(allowedParticipants, id)
                });
            });
        });
    }, [pantansWithoutGuestAndSip]);
    var _d = useState([]), participantsSought = _d[0], setParticipantsSought = _d[1];
    var participantsToShow = useMemo(function () {
        return participantsSought.length > 0 ? participantsSought : allowedParticipants;
    }, [participantsSought, allowedParticipants]);
    var searchHandler = useCallback(debounce(function (e) {
        setParticipantsSought(function () {
            return e.target.value
                ? allowedParticipants.filter(function (participant) {
                    return participant.displayName
                        .toLowerCase()
                        .includes(e.target.value.toLowerCase());
                })
                : allowedParticipants;
        });
    }, 250), [allowedParticipants]);
    var checkAllHandler = function () {
        var someIsFalse = allowedParticipants.some(function (participant) { return participant.isSelected === false; });
        var newStateParticipants = allowedParticipants.map(function (participant) { return (__assign(__assign({}, participant), { isSelected: someIsFalse })); });
        setAlloedParticipants(newStateParticipants);
        setFieldValue(name, getSelectedParticipants(newStateParticipants));
    };
    var checkParticipantHandler = function (e, index) {
        var newStateParticipants = allowedParticipants;
        newStateParticipants[index].isSelected = e.target.checked;
        setAlloedParticipants(newStateParticipants);
        setFieldValue(name, newStateParticipants.filter(function (participant) { return participant.isSelected; }));
    };
    var renderErrors = hasError && (React.createElement(Grid, { item: true },
        React.createElement(FormHelperText, { error: hasError }, error)));
    var renderSearchUser = (React.createElement(Grid, { item: true, xs: 12 },
        React.createElement(TextField, { size: "small", onChange: searchHandler, startAdornment: React.createElement(InputAdornment, { position: "start" },
                React.createElement(SearchIcon, null)), fullWidth: true })));
    var renderAllUsersButton = (React.createElement(Grid, { item: true, xs: 12, display: "flex" },
        React.createElement(Button, { onClick: checkAllHandler, fullWidth: true }, t("poll-participant-list-button-select-all"))));
    var renderParticipant = function (participant, index) { return (React.createElement(ListItem, { key: index },
        React.createElement(Grid, { container: true, spacing: 2, direction: "row", wrap: "nowrap" },
            React.createElement(Grid, { item: true },
                React.createElement(ListItemAvatar, null,
                    React.createElement(ParticipantAvatar, { src: participant.avatarUrl }, participant.displayName))),
            React.createElement(Grid, { item: true, xs: true, zeroMinWidth: true },
                React.createElement(ListItemText, { primary: React.createElement(Typography, { noWrap: true }, participant.displayName) })),
            React.createElement(Grid, { item: true, alignContent: "flex-end" },
                React.createElement(Checkbox, { checked: isSelectedParticipant(allowedParticipants, participant.id), id: participant.id, onChange: function (e) { return checkParticipantHandler(e, index); }, color: "primary" }))))); };
    var renderPartricipants = (React.createElement(Grid, { item: true, xs: 12 },
        React.createElement(CustomList, null, participantsToShow.map(renderParticipant))));
    return (React.createElement(Container$1, { disableGutters: true },
        React.createElement(Grid, { container: true, spacing: 2 },
            renderErrors,
            renderSearchUser,
            renderAllUsersButton,
            renderPartricipants)));
};

var defaultInitialValues = {
    duration: 1,
    enableAbstain: true,
    autoClose: false,
    name: "",
    topic: "",
    allowedParticipants: [],
    subtitle: "",
    createPdf: true,
    kind: "roll_call"
};
var Form = styled(Form$1)({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    width: "100%"
});
var legalVoteOptions = ["roll_call", "live_roll_call", "pseudonymous"];
var CreateLegalVoteForm = function (_a) {
    var _b = _a.initialValues, initialValues = _b === void 0 ? defaultInitialValues : _b, onClose = _a.onClose, isCoffeeBreakActive = _a.isCoffeeBreakActive;
    var t = useTranslation().t;
    var legalVoteId = useRef(useSelector(selectLegalVoteId));
    var dispatch = useDispatch();
    var legalVoteHandle = useContext(LegalVoteContext);
    var validationSchema = yup.object({
        name: yup
            .string()
            .trim()
            .max(150, t("legal-vote-form-input-error-max", { maxCharacters: 150 }))
            .required(t("legal-vote-input-title-required")),
        subtitle: yup
            .string()
            .trim()
            .max(255, t("legal-vote-form-input-error-max", { maxCharacters: 255 })),
        topic: yup
            .string()
            .trim()
            .max(500, t("legal-vote-form-input-error-max", { maxCharacters: 500 })),
        duration: yup
            .number()
            .min(0)
            .nullable(true)
            .typeError(t("legal-vote-form-input-error-number")),
        createPdf: yup.bool(),
        kind: yup.string().oneOf(legalVoteOptions)
    });
    var saveFormValues = useCallback(function (legalVoteFormValues) {
        if (isEmpty(legalVoteFormValues.topic) ||
            isEmpty(legalVoteFormValues.name)) {
            notifications.error(t("legal-vote-save-form-error"));
        }
        else {
            dispatch(saveLegalVoteFormValues(__assign({ id: legalVoteId.current }, legalVoteFormValues)));
            notifications.success(t("legal-vote-save-form-success"));
        }
    }, [dispatch, t]);
    var participantValidationSchema = useMemo(function () {
        return yup.object({
            allowedParticipants: yup
                .array()
                .min(2, t("legal-vote-input-assignments-required"))
                .required(t("legal-vote-input-assignments-required"))
        });
    }, [t]);
    var steps = React.useMemo(function () { return [
        {
            validationSchema: validationSchema,
            component: function (formik) { return (React.createElement(Grid, { container: true, spacing: 1 },
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between" },
                        React.createElement(Typography, { variant: "body2" }, t("legal-vote-form-duration")),
                        React.createElement(DurationField, __assign({}, formikDurationFieldProps("duration", formik), { durationOptions: [null, 1, 2, 5, "custom"], setFieldValue: formik.setFieldValue, ButtonProps: {
                                size: "small"
                            } })))),
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(CommonFormItem, __assign({}, formikSwitchProps("enableAbstain", formik), { control: React.createElement(Switch, { color: "primary" }), label: t("legal-vote-form-allow-abstain"), labelPlacement: "start" }))),
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(CommonFormItem, __assign({}, formikSwitchProps("autoClose", formik), { control: React.createElement(Tooltip, { title: "".concat(t("legal-vote-form-auto-stop-tooltip")) },
                            React.createElement(Switch, { color: "primary" })), label: t("legal-vote-form-auto-stop"), labelPlacement: "start" }))),
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(Select, __assign({}, formikSwitchProps("kind", formik), { defaultValue: formik.initialValues["kind"], id: "vote-kind" }), legalVoteOptions.map(function (kind) { return (React.createElement(MenuItem, { key: kind, value: kind }, t("legal-vote-".concat(kind)))); }))),
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(TextField, __assign({}, formikProps("name", formik), { placeholder: t("legal-vote-input-name-placeholder"), fullWidth: true }))),
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(TextField, __assign({}, formikProps("subtitle", formik), { placeholder: t("legal-vote-input-subtitle-placeholder"), fullWidth: true }))),
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(TextField, __assign({}, formikProps("topic", formik), { minRows: 4, maxRows: 6, multiline: true, placeholder: t("legal-vote-input-topic-placeholder"), fullWidth: true }))))); }
        },
        {
            component: function () { return React.createElement(ParticipantSelector, { name: "allowedParticipants" }); },
            validationSchema: participantValidationSchema
        },
    ]; }, [t]);
    var onSubmit = function (values) {
        var allowedParticipants = values.allowedParticipants;
        dispatch(legalVoteHandle === null || legalVoteHandle === void 0 ? void 0 : legalVoteHandle.apiMessages.start.action({
            name: values.name,
            topic: values.topic,
            subtitle: values.subtitle,
            enableAbstain: values.enableAbstain,
            autoClose: values.autoClose,
            duration: values.duration ? values.duration * 60 : values.duration,
            allowedParticipants: Array.isArray(allowedParticipants)
                ? allowedParticipants.map(function (allowedParticipant) { return allowedParticipant.id; })
                : [],
            createPdf: values.createPdf,
            kind: values.kind,
            timezone: getCurrentTimezone()
        }));
        onClose();
    };
    var renderButtons = function (isLastStep, values, handleNext) { return (React.createElement(Box, { display: "flex", justifyContent: "space-between", gap: 2 },
        !isLastStep && (React.createElement(Button, { type: "button", onClick: function () { return saveFormValues(values); }, fullWidth: true }, t("legal-vote-form-button-save"))),
        React.createElement(Button, { type: "button", disabled: isCoffeeBreakActive, onClick: handleNext, fullWidth: true }, isLastStep
            ? t("poll-participant-list-button-start")
            : t("legal-vote-form-button-continue")))); };
    return (React.createElement(FormikWizard, { enableReinitialize: true, initialValues: initialValues, validateOnNext: true, activeStepIndex: 0, onSubmit: onSubmit, steps: steps, validateOnBlur: false, validateOnChange: false }, function (_a) {
        var renderComponent = _a.renderComponent, handlePrev = _a.handlePrev, handleNext = _a.handleNext, values = _a.values, currentStepIndex = _a.currentStepIndex, isLastStep = _a.isLastStep;
        return (React.createElement(Stack, { flex: 1, overflow: "hidden", spacing: 1 },
            React.createElement(Stack, { flex: 1, spacing: 1, overflow: "hidden" },
                React.createElement(Stack, { alignItems: "flex-start", spacing: 1 },
                    React.createElement(Button, { variant: "text", onClick: currentStepIndex && currentStepIndex > 0
                            ? handlePrev
                            : onClose, startIcon: React.createElement(BackIcon, null), size: "small" }, t("legal-vote-button-back")),
                    React.createElement(Typography, { variant: "h2" }, (initialValues === null || initialValues === void 0 ? void 0 : initialValues.id) !== undefined
                        ? t("legal-vote-header-title-update")
                        : t("legal-vote-header-title-create"))),
                React.createElement(Form, null, renderComponent())),
            renderButtons(isLastStep, values, handleNext)));
    }));
};

function formatSeconds(seconds) {
    var _hours = Math.floor(seconds / 3600);
    var _minutes = Math.floor((seconds - _hours * 3600) / 60);
    var _seconds = seconds - _hours * 3600 - _minutes * 60;
    var output = [
        _hours > 0 ? String(_hours).padStart(2, "0") + ":" : "",
        _minutes > 0 ? String(_minutes).padStart(2, "0") + ":" : "00:",
        _seconds > 0 ? String(_seconds).padStart(2, "0") : "00",
    ];
    return output.join("");
}
function LegalVoteCountdown(_a) {
    var active = _a.active, duration = _a.duration, startTime = _a.startTime, boxProps = __rest(_a, ["active", "duration", "startTime"]);
    var _b = useState(""), counter = _b[0], setCounter = _b[1];
    useEffect(function () {
        if (!active) {
            setCounter("00:00");
            return;
        }
        var startDate = new Date(startTime);
        var endDate = new Date(startDate);
        endDate.setSeconds(startDate.getSeconds() + duration);
        function updateCounter() {
            var delta = Number(endDate) - Date.now();
            if (delta < 0) {
                // stop the timer.
                setCounter("00:00");
                clearInterval(interval);
                return;
            }
            var remainingSeconds = Math.floor(delta / 1000);
            setCounter(formatSeconds(remainingSeconds));
        }
        updateCounter();
        var interval = setInterval(function () {
            updateCounter();
        }, 1000);
        return function () {
            clearInterval(interval);
        };
    }, [duration, startTime, active]);
    return (React.createElement(Box, __assign({ display: "flex", alignItems: "center" }, boxProps),
        React.createElement(DurationIcon, null),
        React.createElement(Typography, { ml: 0.5, sx: { fontVariantNumeric: "tabular-nums" } }, counter)));
}

var MainContainer = styled(Stack)(function (_a) {
    var theme = _a.theme;
    return ({
        backgroundColor: theme.palette.background.paper,
        width: "100%",
        padding: theme.spacing(1.5),
        borderRadius: "0.4rem"
    });
});
var Divider = styled("div")({
    borderTop: "3px solid #193a47"
});
var VoteState = styled("div")(function (_a) {
    var theme = _a.theme, state = _a.state;
    return ({
        backgroundColor: state === "active"
            ? theme.palette.success.main
            : theme.palette.error.main,
        padding: theme.spacing(0.5),
        fontWeight: "bold"
    });
});
var ButtonContainer = styled("div")(function () { return ({
    display: "flex",
    justifyContent: "space-between"
}); });
var TopicTypography = styled(Typography)({
    wordBreak: "break-word"
});
var LegalVoteOverviewPanel = function (_a) {
    var vote = _a.vote;
    var dispatch = useDispatch();
    var t = useTranslation().t;
    var legalVoteHandle = useContext(LegalVoteContext);
    var _b = useState(false), dispatchTriggered = _b[0], setDispatchTriggered = _b[1];
    var handleCancel = function () {
        if (!(vote === null || vote === void 0 ? void 0 : vote.id) || dispatchTriggered) {
            return;
        }
        setDispatchTriggered(true);
        dispatch(legalVoteHandle === null || legalVoteHandle === void 0 ? void 0 : legalVoteHandle.apiMessages.cancel.action({
            legalVoteId: vote === null || vote === void 0 ? void 0 : vote.id,
            reason: "Testing reasons",
            timezone: getCurrentTimezone()
        }));
    };
    var handleEnd = function () {
        if (!(vote === null || vote === void 0 ? void 0 : vote.id) || dispatchTriggered) {
            return;
        }
        setDispatchTriggered(true);
        dispatch(legalVoteHandle === null || legalVoteHandle === void 0 ? void 0 : legalVoteHandle.apiMessages.stop.action({
            legalVoteId: vote === null || vote === void 0 ? void 0 : vote.id,
            timezone: getCurrentTimezone()
        }));
    };
    var startTime = new Date(vote ? vote === null || vote === void 0 ? void 0 : vote.localStartTime : "");
    var endTime = startTime.getTime() + ((vote === null || vote === void 0 ? void 0 : vote.duration) ? vote === null || vote === void 0 ? void 0 : vote.duration : 0) * 1000;
    var formattedStartTime = useDateFormat(startTime, "time");
    var getVotedNumber = function () {
        return (vote === null || vote === void 0 ? void 0 : vote.votingRecord) !== undefined
            ? Object.keys(vote.votingRecord).length
            : 0;
    };
    var MAX_TOPIC_LENGTH = 120;
    return (React.createElement(MainContainer, { spacing: 2 },
        React.createElement(Box, { display: "flex", alignItems: "center", justifyContent: "space-between" },
            React.createElement(Box, { display: "flex", alignItems: "center" },
                React.createElement(VoteState, { state: vote === null || vote === void 0 ? void 0 : vote.state }, t("legal-vote-overview-panel-status-".concat(vote === null || vote === void 0 ? void 0 : vote.state))),
                React.createElement(Typography, { ml: 1 }, "".concat(formattedStartTime))),
            vote && (React.createElement(LegalVoteCountdown, { duration: vote.duration || 0, startTime: vote.startTime, active: vote.state === "active", flex: 1, justifyContent: "flex-end" }))),
        React.createElement(Stack, { spacing: 1 },
            (vote === null || vote === void 0 ? void 0 : vote.duration) && (React.createElement(ProgressBar, { endTime: endTime, startTime: startTime.getTime(), isFinished: Boolean((vote === null || vote === void 0 ? void 0 : vote.state) !== "active") })),
            React.createElement(Divider, null),
            React.createElement(Typography, { noWrap: true, align: "center" }, vote === null || vote === void 0 ? void 0 : vote.name),
            React.createElement(TopicTypography, { variant: "body2", align: "center" }, truncate(vote === null || vote === void 0 ? void 0 : vote.topic, { length: MAX_TOPIC_LENGTH })),
            React.createElement(Box, { display: "flex", justifyContent: "center", alignItems: "center" },
                React.createElement(LegalBallotIcon, null),
                React.createElement(Typography, { ml: 0.5 },
                    getVotedNumber(),
                    "/",
                    (vote === null || vote === void 0 ? void 0 : vote.allowedParticipants.length) || 0)),
            (vote === null || vote === void 0 ? void 0 : vote.state) === "active" && (React.createElement(React.Fragment, null,
                React.createElement(Divider, null),
                React.createElement(ButtonContainer, null,
                    React.createElement(Button, { size: "small", variant: "text", onClick: handleCancel, disabled: dispatchTriggered }, t("legal-vote-overview-panel-button-cancel")),
                    React.createElement(Button, { size: "small", variant: "contained", onClick: handleEnd, disabled: dispatchTriggered }, t("legal-vote-overview-panel-button-end"))))))));
};

var List = styled(List$1)({
    width: "100%"
});
var ListItemButton = styled(ListItemButton$1)(function (_a) {
    var theme = _a.theme;
    return ({
        borderRadius: theme.borderRadius.medium,
        backgroundColor: theme.palette.background.paper,
        "&:hover": {
            backgroundColor: theme.palette.secondary.lighter
        },
        "&:not(:last-child) ": {
            marginBottom: theme.spacing(1)
        }
    });
});
var NoVoteImage = styled(NoVotesIcon)({
    width: "7.5rem",
    height: "7.5rem"
});
var LegalVoteOverview = function (_a) {
    var onClickItem = _a.onClickItem;
    var t = useTranslation().t;
    var _b = useState({
        savedVotes: true,
        createdVotes: true
    }), accordionState = _b[0], setAccordionState = _b[1];
    var votes = useSelector(selectAllVotes);
    var savedLegalVotes = useSelector(selectAllSavedLegalVotes);
    if (votes.length === 0 && savedLegalVotes.length === 0) {
        return (React.createElement(Stack, { flex: 1, spacing: 2, overflow: "auto", justifyContent: "center", alignItems: "center" },
            React.createElement(Box, null,
                React.createElement(NoVoteImage, null)),
            React.createElement(Typography, { align: "center", variant: "body2" }, t("no-votes-in-conference"))));
    }
    var renderSavedLegalVotes = function () { return (React.createElement(AccordionItem, { onChange: function () {
            return setAccordionState(function (prevState) { return (__assign(__assign({}, prevState), { savedVotes: !prevState.savedVotes })); });
        }, option: t("legal-vote-overview-saved-legal-votes"), expanded: accordionState.savedVotes, summaryText: t("legal-vote-overview-saved-legal-votes") },
        React.createElement(List, null, savedLegalVotes.map(function (savedLegalVote, index) { return (React.createElement(ListItemButton, { key: index, onClick: function () { return onClickItem(savedLegalVote.id); } },
            React.createElement(ListItemText, { primary: savedLegalVote.name, secondary: savedLegalVote.topic }))); })))); };
    var renderLegalVotes = function () { return (React.createElement(AccordionItem, { onChange: function () {
            return setAccordionState(function (prevState) { return (__assign(__assign({}, prevState), { createdVotes: !prevState.createdVotes })); });
        }, option: t("legal-vote-overview-created-legal-votes"), expanded: accordionState.createdVotes, summaryText: t("legal-vote-overview-created-legal-votes") },
        React.createElement(List, null, votes.map(function (vote, index) { return (React.createElement(ListItem$1, { key: index, disableGutters: true },
            React.createElement(LegalVoteOverviewPanel, { vote: vote }))); })))); };
    return (React.createElement(Stack, { flex: 1, overflow: "auto" },
        savedLegalVotes.length > 0 && renderSavedLegalVotes(),
        votes.length > 0 && renderLegalVotes()));
};

var LegalVote = function (_a) {
    var currentRoomMode = _a.currentRoomMode;
    var _b = useState(false), showLegalVoteForm = _b[0], setShowLegalVoteForm = _b[1];
    var _c = useState(), savedLegalVoteFormId = _c[0], setSavedLegalVoteFormId = _c[1];
    var isCoffeeBreakActive = currentRoomMode() === "coffee-break";
    var formValues = useSelector(selectSavedLegalVotePerId(savedLegalVoteFormId));
    var t = useTranslation().t;
    var handleOnClickSavedLegalVoteItem = function (id) {
        setSavedLegalVoteFormId(id);
        setShowLegalVoteForm(true);
    };
    var handleOnClose = function () {
        setSavedLegalVoteFormId(undefined);
        setShowLegalVoteForm(false);
    };
    var renderLegalVoteOverview = function () {
        return (React.createElement(Stack, { flex: 1, spacing: 1, overflow: "hidden" },
            React.createElement(LegalVoteOverview, { onClickItem: handleOnClickSavedLegalVoteItem }),
            React.createElement(Button, { onClick: function () { return setShowLegalVoteForm(true); } }, t("legal-vote-overview-button-create-vote"))));
    };
    var renderCreateLegalVoteForm = function () {
        return (React.createElement(CreateLegalVoteForm, { isCoffeeBreakActive: isCoffeeBreakActive, initialValues: formValues, onClose: handleOnClose }));
    };
    return showLegalVoteForm
        ? renderCreateLegalVoteForm()
        : renderLegalVoteOverview();
};

var Container = styled(Box)(function () { return ({
    width: "100%"
}); });
function LegalVoteTokenClipboard(props) {
    var t = useTranslation().t;
    var date = useDateFormat(new Date(props.timestamp), "date");
    var time = useDateFormat(new Date(props.timestamp), "time");
    function injectTokenIntoClipboard() {
        var content = "\n".concat(props.name, "\n").concat(date, "\n").concat(time, "\n").concat(t("legal-vote-success-clipboard-message", { vote: props.vote }), "\n").concat(props.token, "\n        ").trim();
        navigator.clipboard
            .writeText(content)
            .then(function () {
            notifications.success(t("legal-vote-token-copy-success"));
        })["catch"](function (error) {
            console.error(error);
        });
    }
    return (React.createElement(Container, { display: "flex", alignItems: "center", gap: 2 },
        React.createElement(TextField, { name: "token", disabled: true, value: props.token, fullWidth: true }),
        React.createElement(Button, { type: "button", onClick: injectTokenIntoClipboard, "aria-label": "Copy token" },
            React.createElement(CopyIcon, null))));
}

var isVoteOption = function (option) {
    return option === "yes" || option === "no" || option === "abstain";
};

var LegalVoteKind;
(function (LegalVoteKind) {
    LegalVoteKind["RollCall"] = "roll_call";
    LegalVoteKind["LiveRollCall"] = "live_roll_call";
    LegalVoteKind["Pseudonymous"] = "pseudonymous";
})(LegalVoteKind || (LegalVoteKind = {}));
var start = createSignalingApiCall("legal_vote", "start");
var stop = createSignalingApiCall("legal_vote", "stop");
var cancel = createSignalingApiCall("legal_vote", "cancel");
var vote = createSignalingApiCall("legal_vote", "vote");
var createOutgoingLegalVote = function (createModule, sendMessage) {
    var handler = createModule(function (builder) {
        builder.addCase(start.action, function (_state, action) {
            sendMessage(start(action.payload));
        });
        builder.addCase(stop.action, function (_state, action) {
            sendMessage(stop(action.payload));
        });
        builder.addCase(cancel.action, function (_state, action) {
            sendMessage(cancel(action.payload));
        });
        builder.addCase(vote.action, function (_state, action) {
            sendMessage(vote(action.payload));
        });
    });
    return {
        actions: {
            start: start,
            stop: stop,
            cancel: cancel,
            vote: vote
        },
        handler: handler
    };
};

export { AutomodError, AutomodProvider, AutomodSelectHow, LegalVote as LegalVoteComponent, LegalVoteCountdown, LegalVoteError, LegalVoteKind, LegalVodeProviderContext as LegalVoteProvider, LegalVoteTokenClipboard, TalkingStickParticipantList, TalkingStickTabPanel$1 as TalkingStickTabPanel, VoteCancelReasonType, VoteInvalidReason, automodSlice$1 as automodStore, createOutgoingAutomod, createOutgoingLegalVote, createTalkingStickNotification, isError, isVoteOption, legalVoteSlice$1 as legalVoteStore };
